import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  Save,
  FileDown,
  Settings,
  CheckSquare,
  History,
  FileText,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  RotateCcw,
  Trash2,
  Building2,
  Target,
  Sliders,
  CreditCard,
  BarChart3,
  PieChart,
  Plus,
  Calendar,
  TrendingUp,
  Percent,
  MessageSquare,
  Layers,
  HeartPulse,
  Users,
  Rocket,
  Check,
  Circle,
  Play,
} from 'lucide-react';
import {
  useProject,
  useUpdateProject,
  useDeleteProject,
  useSetProjectCapabilities,
  useSetProjectGuardrails,
  useExportProjectPptx,
  useDeliveryPlan,
  useSaveDeliveryPlan,
  useProjectVersions,
  useRestoreProjectVersion,
  useAvailableDeliveryProfiles,
  useApplyDeliveryProfile,
  useEffectiveDeliveryPlan,
  useSubmitProject,
  useApproveProject,
  useRejectProject,
  useReviseProject,
  useResubmitProject,
  useCancelRevision,
  useArchiveProject,
  useUnlockProject,
  useRebaselineProject,
  useUpdateProjectMobilisationStage,
  useMarkProjectLost,
  useReopenProject,
  PROJECT_MOBILISATION_STAGES,
  LOST_REASONS,
  MobilisationStage,
  ProjectMobilisationStageInfo,
  RagStatus,
  LostReason,
} from '@/hooks/useProjects';
import { useWorkstreams, VcfWorkstream, VcfActivity, VcfCapability } from '@/hooks/useWorkstreams';
import { useHasPermission } from '@/hooks/useAuth';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { UserPicker } from '@/components/common/UserPicker';
import { useRateCards, useRateCard } from '@/hooks/useRateCards';
import { useResourceRoles } from '@/hooks/useAdminRoles';
import { WorkstreamTree } from '@/components/vcf/WorkstreamTree';
import { ForecastEditor } from '@/components/vcf/ForecastEditor';
import { GeneralMultipliersPanel } from '@/components/vcf/GeneralMultipliersPanel';
import { ProjectSummaryTab } from '@/components/vcf/ProjectSummaryTab';
import { GanttTimeline } from '@/components/vcf/GanttTimeline';
import { ProjectScopingTimeline } from '@/components/pipeline/ProjectScopingTimeline';
import { cn } from '@/lib/utils';
import { generateWeekAllocations, isWeekOverAllocated, computeWeekResourceTotals, checkDependencyViolation } from '@/lib/generateAllocations';
import { createUndoStack } from '@/lib/undoStack';
import { PHASE_COLOURS as SHARED_PHASE_COLOURS, getSkillColours, getRoleColours } from '@/components/delivery/constants';
import { useSkills } from '@/hooks/useSkills';
import { useCurrentFrameworkVersion } from '@/hooks/useFrameworkChanges';
import { useProjectFramework } from '@/hooks/useProjectFramework';

type TabId = 'overview' | 'delivery' | 'summary' | 'timeline' | 'forecasting' | 'history';

const TABS: Array<{ id: TabId; label: string; icon: typeof CheckSquare }> = [
  { id: 'overview', label: 'Project Overview', icon: Target },
  { id: 'delivery', label: 'Delivery Plan', icon: FileText },
  { id: 'summary', label: 'Summary', icon: PieChart },
  { id: 'timeline', label: 'Timeline', icon: BarChart3 },
  { id: 'forecasting', label: 'Forecasting', icon: TrendingUp },
  { id: 'history', label: 'Version History', icon: History },
];

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  submitted: 'bg-blue-100 text-blue-700',
  in_review: 'bg-yellow-100 text-yellow-700',
  approved: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
  revising: 'bg-amber-100 text-amber-700',
  archived: 'bg-gray-200 text-gray-600',
  lost: 'bg-red-100 text-red-800',
  cancelled: 'bg-orange-100 text-orange-800',
};

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  submitted: 'Submitted',
  in_review: 'In Review',
  approved: 'Approved',
  rejected: 'Rejected',
  revising: 'Revising',
  archived: 'Archived',
  lost: 'Lost',
  cancelled: 'Cancelled',
};

export function ProjectDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: projectData, isLoading: projectLoading } = useProject(id!);
  const { data: workstreamsData, isLoading: workstreamsLoading } = useWorkstreams({
    includeCapabilities: true,
    includeActivities: true,
  });
  const { data: rolesData } = useResourceRoles();
  const { data: currentVersion } = useCurrentFrameworkVersion();
  const projectFramework = useProjectFramework(projectData?.project ?? null);

  // Rate card hooks for Summary tab
  const { data: rateCardsData, isLoading: rateCardsLoading } = useRateCards();
  const [selectedRateCardId, setSelectedRateCardId] = useState<string>('');
  const { data: rateCardData } = useRateCard(selectedRateCardId);

  const updateProject = useUpdateProject();
  const deleteProject = useDeleteProject();
  const submitProject = useSubmitProject();
  const approveProject = useApproveProject();
  const rejectProject = useRejectProject();
  const reviseProject = useReviseProject();
  const resubmitProject = useResubmitProject();
  const cancelRevision = useCancelRevision();
  const archiveProject = useArchiveProject();
  const reopenProject = useReopenProject();
  const [showMarkLostModal, setShowMarkLostModal] = useState(false);
  const unlockProject = useUnlockProject();
  const rebaselineProject = useRebaselineProject();
  const canSubmit = useHasPermission('projects.submit');
  const canApprove = useHasPermission('projects.approve');
  const canReject = useHasPermission('projects.reject');
  const canRevise = useHasPermission('projects.revise');
  const canArchive = useHasPermission('projects.archive');
  const canUnlock = useHasPermission('projects.unlock');
  const canRebaseline = useHasPermission('projects.rebaseline');
  const canEdit = useHasPermission('projects.update');
  const canEditDelivery = useHasPermission('projects.delivery_plan.edit');
  const setCapabilities = useSetProjectCapabilities();
  const setGuardrails = useSetProjectGuardrails();
  const exportPptx = useExportProjectPptx();
  const saveDeliveryPlan = useSaveDeliveryPlan();
  const { data: profilesData } = useAvailableDeliveryProfiles();
  const applyProfile = useApplyDeliveryProfile();
  const [selectedProfileId, setSelectedProfileId] = useState<string>('');
  const [showApplyDialog, setShowApplyDialog] = useState(false);
  const [applyOptions, setApplyOptions] = useState({ capabilities: true, guardrails: true, allocations: false });

  // Fetch delivery plan data
  const { data: deliveryPlanData } = useDeliveryPlan(id!);

  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_hasDeliveryChanges, setHasDeliveryChanges] = useState(false);
  const [selectedCapabilityId, setSelectedCapabilityId] = useState<string | null>(null);

  // Auto-save state
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Local state for editing
  const [selectedCapabilities, setSelectedCapabilities] = useState<Set<string>>(new Set());
  const [guardrailValues, setGuardrailValues] = useState<Record<string, string>>({});

  // Project details form state for Overview tab
  const [projectFormData, setProjectFormData] = useState({
    name: '',
    description: '',
    deliveryLocation: 'Remote' as 'Remote' | 'Onsite' | 'Hybrid' | 'Manual',
    currency: 'GBP' as 'GBP' | 'USD' | 'EUR',
    engagementStartDate: '',
    daysPerWeek: 5,
    architectOversightPct: 10,
    pmPct: 20,
    bcArchitectOversightPct: 5,
    bcPmPct: 5,
    healthStatus: 'na' as RagStatus,
    healthNotes: '',
    scopingLeadId: null as string | null,
    scopingLeadName: '',
    deliveryLeadId: null as string | null,
    deliveryLeadName: '',
  });

  // Delivery plan state - will be passed to DeliveryPlanTab and updated via callbacks
  const [deliveryAllocations, setDeliveryAllocations] = useState<Record<string, { weekAllocations: Record<number, number>; baseDaysOverride?: number; roleOverride?: string }>>({});
  const [deliveryStartWeeks, setDeliveryStartWeeks] = useState<Record<string, number>>({});

  // Initialize from project data
  useEffect(() => {
    if (projectData?.project && workstreamsData?.workstreams) {
      const caps = new Set(
        projectData.project.capabilities
          .filter((c) => c.isIncluded)
          .map((c) => c.capabilityId)
      );
      setSelectedCapabilities(caps);

      // Build guardrail values from saved data, falling back to admin defaults
      const savedGuardrails: Record<string, string> = {};
      for (const gr of projectData.project.guardrailInputs) {
        savedGuardrails[gr.guardrailId] = gr.value;
      }

      // Initialize with admin defaults for all guardrails from all capabilities
      const grs: Record<string, string> = {};
      for (const ws of workstreamsData.workstreams) {
        for (const cap of ws.capabilities || []) {
          for (const gr of cap.guardrails || []) {
            // Use saved value if exists, otherwise use admin default
            grs[gr.id] = savedGuardrails[gr.id] ?? gr.defaultValue ?? '';
          }
        }
      }
      setGuardrailValues(grs);

      // Initialize project form data
      setProjectFormData({
        name: projectData.project.name,
        description: projectData.project.description || '',
        deliveryLocation: (projectData.project.deliveryLocation || 'Remote') as 'Remote' | 'Onsite' | 'Hybrid' | 'Manual',
        currency: (projectData.project.currency || 'GBP') as 'GBP' | 'USD' | 'EUR',
        engagementStartDate: projectData.project.engagementStartDate || '',
        daysPerWeek: projectData.project.daysPerWeek || 5,
        architectOversightPct: projectData.project.architectOversightPct / 100 || 10,
        pmPct: projectData.project.pmPct / 100 || 20,
        bcArchitectOversightPct: projectData.project.bcArchitectOversightPct / 100 || 5,
        bcPmPct: projectData.project.bcPmPct / 100 || 5,
        healthStatus: (projectData.project.healthStatus || 'na') as RagStatus,
        healthNotes: projectData.project.healthNotes || '',
        scopingLeadId: projectData.project.scopingLeadId || null,
        scopingLeadName: projectData.project.scopingLeadName || '',
        deliveryLeadId: projectData.project.deliveryLeadId || null,
        deliveryLeadName: projectData.project.deliveryLeadName || '',
      });
    }
  }, [projectData, workstreamsData]);

  // Set default rate card on load
  useEffect(() => {
    const rateCards = rateCardsData?.rateCards || [];
    if (rateCards.length > 0 && !selectedRateCardId) {
      const fundingTypeId = projectData?.project?.fundingTypeId;
      const defaultCard = rateCards.find(rc => rc.isDefault && rc.typeId === fundingTypeId)
        || rateCards.find(rc => rc.isDefault)
        || rateCards[0];
      setSelectedRateCardId(defaultCard.id);
    }
  }, [rateCardsData, selectedRateCardId]);

  // Initialize delivery plan from API data
  useEffect(() => {
    if (deliveryPlanData) {
      // Convert API allocations to local state format
      const allocs: Record<string, { weekAllocations: Record<number, number>; baseDaysOverride?: number; roleOverride?: string }> = {};

      for (const a of deliveryPlanData.allocations) {
        if (!allocs[a.activityId]) {
          allocs[a.activityId] = { weekAllocations: {} };
        }
        allocs[a.activityId].weekAllocations[a.week] = a.days;
      }

      // Add overrides (API returns days, local state stores days)
      for (const o of deliveryPlanData.activityOverrides) {
        if (!allocs[o.activityId]) {
          allocs[o.activityId] = { weekAllocations: {} };
        }
        if (o.overrideDays !== null) allocs[o.activityId].baseDaysOverride = o.overrideDays;
        if (o.overrideRole !== null) allocs[o.activityId].roleOverride = o.overrideRole;
      }

      setDeliveryAllocations(allocs);
      setDeliveryStartWeeks(deliveryPlanData.capabilityStartWeeks);
    }
  }, [deliveryPlanData]);

  // Auto-save handler
  const handleAutoSave = useCallback(async () => {
    if (!id) return;
    setIsSaving(true);
    try {
      // Save project settings
      await updateProject.mutateAsync({
        id,
        name: projectFormData.name,
        description: projectFormData.description,
        deliveryLocation: projectFormData.deliveryLocation,
        currency: projectFormData.currency,
        engagementStartDate: projectFormData.engagementStartDate || undefined,
        daysPerWeek: projectFormData.daysPerWeek,
        architectOversightPct: projectFormData.architectOversightPct * 100,
        pmPct: projectFormData.pmPct * 100,
        healthStatus: projectFormData.healthStatus,
        healthNotes: projectFormData.healthNotes || null,
        scopingLeadId: projectFormData.scopingLeadId,
        scopingLeadName: projectFormData.scopingLeadName || null,
        deliveryLeadId: projectFormData.deliveryLeadId,
        deliveryLeadName: projectFormData.deliveryLeadName || null,
      });

      // Save capabilities
      await setCapabilities.mutateAsync({
        projectId: id,
        capabilities: Array.from(selectedCapabilities).map((capId) => ({
          capabilityId: capId,
          isIncluded: true,
        })),
      });

      // Save guardrails
      await setGuardrails.mutateAsync({
        projectId: id,
        guardrails: Object.entries(guardrailValues).map(([guardrailId, value]) => ({
          guardrailId,
          value,
        })),
      });

      // Save delivery plan with auto-save description
      const allocationsForApi = Object.entries(deliveryAllocations).map(([activityId, data]) => ({
        activityId,
        weekAllocations: Object.fromEntries(
          Object.entries(data.weekAllocations).map(([week, days]) => [week, days])
        ),
        baseDaysOverride: data.baseDaysOverride,
        roleOverride: data.roleOverride,
      }));

      await saveDeliveryPlan.mutateAsync({
        projectId: id,
        allocations: allocationsForApi,
        capabilityStartWeeks: deliveryStartWeeks,
        changeDescription: 'Auto-saved',
      });

      setLastSavedAt(new Date());
      setHasChanges(false);
      setHasDeliveryChanges(false);
    } catch (error) {
      console.error('Auto-save failed:', error);
    } finally {
      setIsSaving(false);
    }
  }, [id, projectFormData, selectedCapabilities, guardrailValues, deliveryAllocations, deliveryStartWeeks, updateProject, setCapabilities, setGuardrails, saveDeliveryPlan]);

  // Auto-save effect (30 second debounce after changes)
  useEffect(() => {
    if (hasChanges && !isSaving) {
      // Clear any pending auto-save
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }

      // Schedule new auto-save after 30 seconds
      autoSaveTimerRef.current = setTimeout(() => {
        handleAutoSave();
      }, 30000);
    }

    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [hasChanges, isSaving, handleAutoSave]);

  // Warn before leaving with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasChanges]);

  const handleCapabilityToggle = (capabilityId: string, included: boolean) => {
    const newSet = new Set(selectedCapabilities);
    if (included) {
      newSet.add(capabilityId);
    } else {
      newSet.delete(capabilityId);
    }
    setSelectedCapabilities(newSet);
    setHasChanges(true);
  };

  const handleGuardrailChange = (guardrailId: string, value: string) => {
    setGuardrailValues((prev) => ({ ...prev, [guardrailId]: value }));
    setHasChanges(true);
  };

  // Handler for project form field updates
  const handleProjectFormChange = <K extends keyof typeof projectFormData>(
    field: K,
    value: (typeof projectFormData)[K]
  ) => {
    setProjectFormData((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setIsSaving(true);
    if (!id) return;

    try {
      // Save project settings
      await updateProject.mutateAsync({
        id,
        name: projectFormData.name,
        description: projectFormData.description,
        deliveryLocation: projectFormData.deliveryLocation,
        currency: projectFormData.currency,
        engagementStartDate: projectFormData.engagementStartDate || undefined,
        daysPerWeek: projectFormData.daysPerWeek,
        architectOversightPct: projectFormData.architectOversightPct * 100,
        pmPct: projectFormData.pmPct * 100,
        healthStatus: projectFormData.healthStatus,
        healthNotes: projectFormData.healthNotes || null,
        scopingLeadId: projectFormData.scopingLeadId,
        scopingLeadName: projectFormData.scopingLeadName || null,
        deliveryLeadId: projectFormData.deliveryLeadId,
        deliveryLeadName: projectFormData.deliveryLeadName || null,
      });

      // Save capabilities
      await setCapabilities.mutateAsync({
        projectId: id,
        capabilities: Array.from(selectedCapabilities).map((capId) => ({
          capabilityId: capId,
          isIncluded: true,
        })),
      });

      // Save guardrails
      await setGuardrails.mutateAsync({
        projectId: id,
        guardrails: Object.entries(guardrailValues).map(([guardrailId, value]) => ({
          guardrailId,
          value,
        })),
      });

      // Always save delivery plan (allocations and start weeks)
      const allocationsForApi = Object.entries(deliveryAllocations).map(([activityId, data]) => ({
        activityId,
        weekAllocations: Object.fromEntries(
          Object.entries(data.weekAllocations).map(([week, days]) => [week, days])
        ),
        baseDaysOverride: data.baseDaysOverride,
        roleOverride: data.roleOverride,
      }));

      await saveDeliveryPlan.mutateAsync({
        projectId: id,
        allocations: allocationsForApi,
        capabilityStartWeeks: deliveryStartWeeks,
        changeDescription: 'Project saved',
      });

      setLastSavedAt(new Date());
      setHasChanges(false);
      setHasDeliveryChanges(false);
    } catch (error) {
      console.error('Failed to save:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    try {
      await deleteProject.mutateAsync(id);
      navigate('/projects');
    } catch (error) {
      console.error('Failed to delete:', error);
    }
  };

  const workstreams = workstreamsData?.workstreams || [];
  const project = projectData?.project;

  const isLoading = projectLoading || workstreamsLoading;

  // Calculate pricing summary
  const pricingSummary = useMemo(() => {
    let totalBaseDays = 0;
    let totalCostCents = 0;
    const DEFAULT_RATE = 150000; // fallback: $1500/day in cents

    for (const ws of workstreams) {
      for (const cap of ws.capabilities || []) {
        if (selectedCapabilities.has(cap.id)) {
          for (const act of cap.activities || []) {
            const days = act.baseDays / 100;
            totalBaseDays += act.baseDays;
            totalCostCents += days * DEFAULT_RATE;
          }
        }
      }
    }

    const baseDays = totalBaseDays / 100;
    const adjustedDays = baseDays;
    const architectOversight = adjustedDays * ((project?.architectOversightPct || 1000) / 10000);
    const pm = adjustedDays * ((project?.pmPct || 2000) / 10000);
    const bcArchitect = adjustedDays * ((project?.bcArchitectOversightPct || 500) / 10000);
    const bcPm = adjustedDays * ((project?.bcPmPct || 500) / 10000);
    const totalDays = adjustedDays + architectOversight + pm + bcArchitect + bcPm;

    return {
      baseDays,
      adjustedDays,
      architectOversight,
      pm,
      totalDays,
      // Note: totalCostCents is approximate (uses default rate). The Summary tab
      // calculates the precise cost using rate card rates. This is just for the
      // header/footer display when no rate card calculation has run.
      totalCostCents,
    };
  }, [workstreams, selectedCapabilities, project]);

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="rounded-lg bg-red-50 p-4 text-red-700">
        Project not found
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1.5 md:gap-2 text-xs md:text-sm text-gray-500 mb-3 md:mb-4 overflow-x-auto">
        <Link to="/opportunities" className="hover:text-xtravirt-cyan transition-colors whitespace-nowrap">
          Pipeline
        </Link>
        <ChevronRight className="h-3 w-3 md:h-4 md:w-4 flex-shrink-0" />
        {project.opportunity ? (
          <>
            <Link
              to={`/opportunities/${project.opportunity.id}`}
              className="hover:text-xtravirt-cyan transition-colors truncate max-w-[150px] md:max-w-none"
            >
              {project.opportunity.title}
            </Link>
            <ChevronRight className="h-3 w-3 md:h-4 md:w-4 flex-shrink-0" />
          </>
        ) : null}
        <span className="text-xtravirt-dark font-medium truncate">{project.name}</span>
      </nav>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 md:gap-4 border-b border-gray-200 pb-4">
        <div className="flex items-start md:items-center gap-3">
          <button
            onClick={() => {
              if (project.opportunity) {
                navigate(`/opportunities/${project.opportunity.id}`);
              } else {
                navigate('/opportunities');
              }
            }}
            className="rounded-md p-2 text-gray-500 hover:bg-gray-100 transition-colors touch-target flex-shrink-0"
            title="Back to opportunity"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2 md:gap-3">
              <h1 className="font-heading text-lg md:text-xl font-bold text-xtravirt-dark truncate">
                {project.name}
              </h1>
              <span
                className={cn(
                  'rounded-full px-2 md:px-2.5 py-0.5 text-xs font-medium flex-shrink-0',
                  STATUS_COLORS[project.status]
                )}
              >
                {STATUS_LABELS[project.status]}
              </span>
              {(() => {
                // Locked projects: show version from snapshot. Draft: show current live version.
                const isLocked = projectFramework.source === 'snapshot';
                const versionNum = isLocked ? projectFramework.frameworkVersionNumber : currentVersion?.versionNumber;
                if (versionNum == null) return null;
                return (
                  <span
                    className={cn(
                      'inline-flex items-center gap-1 text-[10px] md:text-xs px-1.5 md:px-2 py-0.5 rounded-full font-medium flex-shrink-0',
                      isLocked
                        ? 'bg-blue-50 text-blue-700 border border-blue-200'
                        : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                    )}
                    title={isLocked ? 'Locked framework snapshot' : 'Current live framework'}
                  >
                    {isLocked && <span>🔒</span>}
                    Framework v{versionNum}{isLocked ? '' : ' (live)'}
                  </span>
                );
              })()}
              {/* Save Status Indicator */}
              <div className="hidden sm:flex items-center gap-1.5 text-xs md:text-sm">
                {isSaving ? (
                  <>
                    <span className="animate-spin text-gray-500">◐</span>
                    <span className="text-gray-500">Saving...</span>
                  </>
                ) : hasChanges ? (
                  <>
                    <span className="text-amber-500">●</span>
                    <span className="text-amber-600">Unsaved</span>
                  </>
                ) : lastSavedAt ? (
                  <>
                    <span className="text-green-500">✓</span>
                    <span className="text-gray-500 hidden md:inline">Saved {formatRelativeTime(lastSavedAt)}</span>
                  </>
                ) : null}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 md:gap-3 mt-0.5">
              <div className="flex items-center gap-1 text-xs md:text-sm text-gray-600">
                <Building2 className="h-3 w-3 md:h-3.5 md:w-3.5" />
                <span className="truncate max-w-[120px] md:max-w-none">{project.customerName}</span>
              </div>
              {project.opportunity && (
                <Link
                  to={`/opportunities/${project.opportunity.id}`}
                  className="hidden sm:flex items-center gap-1 text-xs md:text-sm text-xtravirt-cyan hover:underline"
                >
                  <FileText className="h-3 w-3 md:h-3.5 md:w-3.5" />
                  <span className="truncate max-w-[150px]">{project.opportunity.title}</span>
                </Link>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 md:gap-3 ml-11 md:ml-0 flex-wrap">
          {/* Lifecycle action buttons by status */}
          {project.status === 'draft' && canSubmit && (
            <button
              onClick={() => {
                if (confirm('Submit this project for review? The framework will be locked at the current state.')) {
                  submitProject.mutate(id!);
                }
              }}
              disabled={submitProject.isPending}
              className="btn-primary flex items-center gap-1 md:gap-2 text-sm"
            >
              <Save className="h-4 w-4" />
              <span className="hidden md:inline">Submit</span>
            </button>
          )}
          {project.status === 'submitted' && (
            <>
              {canApprove && (
                <button
                  onClick={() => {
                    if (confirm('Mark this project as approved by the customer?')) {
                      approveProject.mutate(id!);
                    }
                  }}
                  disabled={approveProject.isPending}
                  className="btn-primary bg-green-600 hover:bg-green-700 flex items-center gap-1 md:gap-2 text-sm"
                >
                  <span className="hidden md:inline">Approve</span>
                </button>
              )}
              {canReject && (
                <button
                  onClick={() => {
                    const reason = prompt('Reason for rejection (optional):');
                    if (reason !== null) {
                      rejectProject.mutate({ id: id!, reason: reason || undefined });
                    }
                  }}
                  disabled={rejectProject.isPending}
                  className="btn-secondary text-red-600 hover:bg-red-50 flex items-center gap-1 md:gap-2 text-sm"
                >
                  <span className="hidden md:inline">Reject</span>
                </button>
              )}
            </>
          )}
          {project.status === 'rejected' && canRevise && (
            <button
              onClick={() => reviseProject.mutate(id!)}
              disabled={reviseProject.isPending}
              className="btn-primary flex items-center gap-1 md:gap-2 text-sm"
            >
              <span className="hidden md:inline">Revise</span>
            </button>
          )}
          {project.status === 'revising' && (
            <>
              {canSubmit && (
                <button
                  onClick={() => {
                    if (confirm('Resubmit revised project for review?')) {
                      resubmitProject.mutate(id!);
                    }
                  }}
                  disabled={resubmitProject.isPending}
                  className="btn-primary flex items-center gap-1 md:gap-2 text-sm"
                >
                  <span className="hidden md:inline">Resubmit</span>
                </button>
              )}
              <button
                onClick={() => {
                  if (confirm('Cancel revision and revert to rejected? Your changes will be lost.')) {
                    cancelRevision.mutate(id!);
                  }
                }}
                className="btn-secondary text-sm"
              >
                <span className="hidden md:inline">Cancel revision</span>
              </button>
            </>
          )}
          {project.status === 'approved' && canArchive && (
            <button
              onClick={() => {
                if (confirm('Archive this approved project?')) {
                  archiveProject.mutate(id!);
                }
              }}
              className="btn-secondary text-sm"
            >
              <span className="hidden md:inline">Archive</span>
            </button>
          )}
          {/* Mark as Lost / Cancelled — available for non-terminal statuses */}
          {project.status !== 'lost' && project.status !== 'cancelled' && project.status !== 'archived' && canEdit && (
            <button
              onClick={() => setShowMarkLostModal(true)}
              className="btn-secondary text-red-600 hover:bg-red-50 text-sm"
              title="Mark as lost or cancelled"
            >
              <span className="hidden md:inline">Mark Lost</span>
            </button>
          )}
          {/* Reopen — available for lost/cancelled */}
          {(project.status === 'lost' || project.status === 'cancelled') && canEdit && (
            <button
              onClick={() => {
                if (confirm('Reopen this project? It will return to draft status.')) {
                  reopenProject.mutate(id!);
                }
              }}
              className="btn-secondary text-sm"
              title="Reopen project"
            >
              <RotateCcw className="h-4 w-4 md:mr-2" />
              <span className="hidden md:inline">Reopen</span>
            </button>
          )}
          {/* Admin: unlock + rebaseline (only when locked) */}
          {project.status !== 'draft' && (
            <>
              {canUnlock && (
                <button
                  onClick={() => {
                    if (confirm('⚠️ Unlocking discards the framework snapshot and reverts to live framework data. Any framework changes since submission will now appear. This is irreversible. Are you sure?')) {
                      unlockProject.mutate(id!);
                    }
                  }}
                  className="btn-secondary text-amber-600 hover:bg-amber-50 text-sm"
                  title="Admin: Unlock to draft (discards snapshot)"
                >
                  <span className="hidden md:inline">Unlock</span>
                </button>
              )}
              {canRebaseline && (
                <button
                  onClick={() => {
                    if (confirm('Refresh the framework snapshot to the current framework state? The snapshot will be replaced with current framework data.')) {
                      rebaselineProject.mutate(id!);
                    }
                  }}
                  className="btn-secondary text-sm"
                  title="Admin: Re-baseline (refresh snapshot from current framework)"
                >
                  <span className="hidden md:inline">Re-baseline</span>
                </button>
              )}
            </>
          )}
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="btn-secondary flex items-center gap-1 md:gap-2 text-red-600 hover:bg-red-50 hover:border-red-300 text-sm"
          >
            <Trash2 className="h-4 w-4" />
            <span className="hidden md:inline">Delete</span>
          </button>
          <button
            onClick={() => exportPptx.mutate({ projectId: id!, projectName: project.name })}
            disabled={exportPptx.isPending}
            className="btn-secondary flex items-center gap-1 md:gap-2 text-sm"
          >
            <FileDown className="h-4 w-4" />
            <span className="hidden md:inline">{exportPptx.isPending ? 'Exporting...' : 'Export'}</span>
          </button>
          <button
            onClick={handleSave}
            disabled={!hasChanges || isSaving || !canEditDelivery || (project.status !== 'draft' && project.status !== 'revising')}
            className={cn(
              'btn-primary flex items-center gap-1 md:gap-2 text-sm',
              (!hasChanges || !canEditDelivery || (project.status !== 'draft' && project.status !== 'revising')) && 'opacity-50 cursor-not-allowed'
            )}
          >
            <Save className="h-4 w-4" />
            <span className="hidden md:inline">{isSaving ? 'Saving...' : 'Save'}</span>
          </button>
        </div>
      </div>

      {/* Lock status banner */}
      {project.status !== 'draft' && project.frameworkSnapshotAt && (
        <div className={cn(
          'mt-3 rounded-lg border px-4 py-2 flex flex-wrap items-center gap-2 text-sm',
          project.status === 'revising' ? 'border-amber-300 bg-amber-50 text-amber-900' : 'border-blue-300 bg-blue-50 text-blue-900'
        )}>
          <span>🔒</span>
          <span className="font-medium">Framework locked</span>
          <span className="text-gray-600">— snapshot from {new Date(project.frameworkSnapshotAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
          {project.status === 'revising' && <span className="text-amber-700">— LOE/allocations editable, framework structure locked</span>}
          {project.rejectionReason && project.status === 'rejected' && (
            <span className="text-red-700 ml-2">Reason: {project.rejectionReason}</span>
          )}
        </div>
      )}

      {/* Project Summary Bar */}
      <div className="summary-bar my-3 md:my-4">
        <div className="flex flex-wrap gap-3 md:gap-4">
          <div className="summary-stat">
            <p className="summary-stat-label">Total Days</p>
            <p className="summary-stat-value">{(project.totalAdjustedDays / 100).toFixed(1)}</p>
          </div>
          <div className="summary-stat">
            <p className="summary-stat-label">Total Hours</p>
            <p className="summary-stat-value">{((project.totalAdjustedDays / 100) * 8).toFixed(0)}</p>
          </div>
          <div className="summary-stat">
            <p className="summary-stat-label">Duration</p>
            <p className="summary-stat-value">
              {Math.ceil((project.totalAdjustedDays / 100) / (project.daysPerWeek || 5))} wks
            </p>
          </div>
          <div className="summary-stat">
            <p className="summary-stat-label">Capabilities</p>
            <p className="summary-stat-value">{selectedCapabilities.size}</p>
          </div>
          <div className="summary-stat">
            <p className="summary-stat-label">Services Cost</p>
            <p className="summary-stat-value success">
              {formatCurrency(project.totalCost || pricingSummary.totalCostCents, project.currency || 'GBP')}
            </p>
          </div>
          <div className="summary-stat">
            <p className="summary-stat-label">Funding</p>
            <p className="summary-stat-value">{project.fundingTypeName || project.fundingMechanism || 'TBC'}</p>
          </div>
        </div>
      </div>

      {/* Tabs - horizontal scroll on mobile */}
      <div className="border-b border-gray-200 overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
        <nav className="flex gap-4 md:gap-6 min-w-max">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                'flex items-center gap-1.5 md:gap-2 border-b-2 py-3 text-xs md:text-sm font-medium transition-colors whitespace-nowrap touch-target',
                activeTab === tab.id
                  ? 'border-xtravirt-cyan text-xtravirt-cyan'
                  : 'border-transparent text-gray-600 hover:text-xtravirt-dark'
              )}
            >
              <tab.icon className="h-4 w-4" />
              <span className="hidden sm:inline">{tab.label}</span>
              <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Main Content with Sidebar */}
      <div className="flex-1 flex flex-col md:flex-row overflow-hidden mt-3 md:mt-4">
        {/* Workstream Sidebar - only shown for delivery tab, hidden on mobile */}
        {activeTab === 'delivery' && (
          <div className="hidden md:block w-72 border-r border-gray-200 flex-shrink-0 overflow-hidden py-4 pr-4">
            <WorkstreamTree
              workstreams={workstreams}
              selectedCapabilities={selectedCapabilities}
              selectedCapabilityId={selectedCapabilityId}
              onCapabilitySelect={setSelectedCapabilityId}
              onCapabilityToggle={handleCapabilityToggle}
            />
          </div>
        )}

        {/* Main Panel */}
        <div className="flex-1 overflow-y-auto p-4">
          {activeTab === 'overview' && (
            <div className="space-y-6 md:space-y-8">
              {/* Project Details Section */}
              <div className="settings-section">
                <div className="settings-section-header">
                  <Settings className="settings-section-icon" />
                  <h3 className="settings-section-title">Project Details</h3>
                </div>
                <div className="card-form space-y-4">
                  <div>
                    <label className="label">Project Name</label>
                    <input
                      type="text"
                      value={projectFormData.name}
                      onChange={(e) => handleProjectFormChange('name', e.target.value)}
                      className="input mt-1 w-full"
                    />
                  </div>
                  <div>
                    <label className="label">Description</label>
                    <textarea
                      value={projectFormData.description}
                      onChange={(e) => handleProjectFormChange('description', e.target.value)}
                      rows={3}
                      className="input mt-1 w-full"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="label">Funding Mechanism</label>
                      <p className="mt-1 text-sm font-medium text-gray-700 px-3 py-2 bg-gray-50 rounded-md border border-gray-200">
                        {project.fundingTypeName || project.fundingMechanism || 'TBC'}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">Inherited from opportunity</p>
                    </div>
                    <div>
                      <label className="label">Delivery Location</label>
                      <select
                        value={projectFormData.deliveryLocation}
                        onChange={(e) => handleProjectFormChange('deliveryLocation', e.target.value as 'Remote' | 'Onsite' | 'Hybrid' | 'Manual')}
                        className="input mt-1 w-full"
                      >
                        <option value="Remote">Remote</option>
                        <option value="Onsite">Onsite</option>
                        <option value="Hybrid">Hybrid</option>
                        <option value="Manual">Manual</option>
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="label">Currency</label>
                      <select
                        value={projectFormData.currency}
                        onChange={(e) => handleProjectFormChange('currency', e.target.value as 'GBP' | 'USD' | 'EUR')}
                        className="input mt-1 w-full"
                      >
                        <option value="GBP">GBP</option>
                        <option value="USD">USD</option>
                        <option value="EUR">EUR</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Service Package Section */}
              <div className="settings-section">
                <div className="settings-section-header">
                  <Layers className="settings-section-icon" />
                  <h3 className="settings-section-title">Service Package</h3>
                </div>
                <div className="card-form space-y-4">
                  <div>
                    <label className="label">Package</label>
                    <div className="flex gap-3 items-end">
                      <select
                        value={selectedProfileId || project.deliveryProfileId || ''}
                        onChange={(e) => setSelectedProfileId(e.target.value)}
                        className="input mt-1 flex-1"
                      >
                        <option value="">No package selected</option>
                        {profilesData?.profiles.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                          </option>
                        ))}
                      </select>
                      <button
                        onClick={() => setShowApplyDialog(true)}
                        disabled={!selectedProfileId && !project.deliveryProfileId}
                        className="btn-primary btn-sm whitespace-nowrap"
                      >
                        Apply Package
                      </button>
                    </div>
                    {project.deliveryProfileId && (
                      <p className="text-xs text-gray-500 mt-2">
                        Currently using: <span className="font-medium text-xtravirt-navy">{profilesData?.profiles.find(p => p.id === project.deliveryProfileId)?.name || 'Unknown'}</span>
                      </p>
                    )}
                    {profilesData?.profiles.find(p => p.id === (selectedProfileId || project.deliveryProfileId))?.description && (
                      <p className="text-xs text-gray-400 mt-1">
                        {profilesData.profiles.find(p => p.id === (selectedProfileId || project.deliveryProfileId))?.description}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Apply Service Package Dialog */}
              {showApplyDialog && (
                <div className="dialog-overlay" onClick={() => setShowApplyDialog(false)}>
                  <div className="dialog" onClick={(e) => e.stopPropagation()}>
                    <div className="dialog-header">
                      <h2>Apply Service Package</h2>
                      <button onClick={() => setShowApplyDialog(false)} className="text-gray-400 hover:text-gray-600">&times;</button>
                    </div>
                    <div className="dialog-body space-y-4">
                      <p className="text-sm text-gray-600">
                        Applying a package will replace your current project settings for the selected items. Your existing data will be saved as a version.
                      </p>
                      <div className="space-y-3">
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={applyOptions.capabilities}
                            onChange={(e) => setApplyOptions(prev => ({ ...prev, capabilities: e.target.checked }))}
                            className="rounded"
                          />
                          <span className="text-sm font-medium">Capabilities (included/excluded)</span>
                        </label>
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={applyOptions.guardrails}
                            onChange={(e) => setApplyOptions(prev => ({ ...prev, guardrails: e.target.checked }))}
                            className="rounded"
                          />
                          <span className="text-sm font-medium">Guardrail default values</span>
                        </label>
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={applyOptions.allocations}
                            onChange={(e) => setApplyOptions(prev => ({ ...prev, allocations: e.target.checked }))}
                            className="rounded"
                          />
                          <span className="text-sm font-medium">Delivery allocations (week-by-week schedule)</span>
                        </label>
                      </div>
                    </div>
                    <div className="dialog-footer">
                      <button onClick={() => setShowApplyDialog(false)} className="btn-ghost btn-sm">Cancel</button>
                      <button
                        onClick={() => {
                          const profileToApply = selectedProfileId || project.deliveryProfileId;
                          if (profileToApply) {
                            applyProfile.mutate({
                              projectId: project.id,
                              profileId: profileToApply,
                              applyCapabilities: applyOptions.capabilities,
                              applyGuardrails: applyOptions.guardrails,
                              applyAllocations: applyOptions.allocations,
                            }, {
                              onSuccess: () => setShowApplyDialog(false),
                            });
                          }
                        }}
                        disabled={applyProfile.isPending}
                        className="btn-primary btn-sm"
                      >
                        {applyProfile.isPending ? 'Applying...' : 'Apply Package'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Configuration Section */}
              <div className="settings-section">
                <div className="settings-section-header">
                  <Sliders className="settings-section-icon" />
                  <h3 className="settings-section-title">Configuration</h3>
                </div>
                <div className="card-form space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="label">
                        <Calendar className="inline h-3.5 w-3.5 mr-1" />
                        Engagement Start Date
                      </label>
                      <input
                        type="date"
                        value={projectFormData.engagementStartDate}
                        onChange={(e) => handleProjectFormChange('engagementStartDate', e.target.value)}
                        className="input mt-1 w-full"
                      />
                    </div>
                    <div>
                      <label className="label">Days per Week</label>
                      <input
                        type="number"
                        min={1}
                        max={7}
                        value={projectFormData.daysPerWeek}
                        onChange={(e) => handleProjectFormChange('daysPerWeek', parseInt(e.target.value))}
                        className="input mt-1 w-full"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Rate Card & Overhead Section */}
              <div className="settings-section">
                <div className="settings-section-header">
                  <CreditCard className="settings-section-icon" />
                  <h3 className="settings-section-title">Rate Card & Overhead</h3>
                </div>
                <div className="card-form space-y-4">
                  <div>
                    <label className="label">Rate Card</label>
                    <select
                      value={selectedRateCardId}
                      onChange={(e) => setSelectedRateCardId(e.target.value)}
                      className="input mt-1 w-full"
                      disabled={rateCardsLoading}
                    >
                      {rateCardsLoading ? (
                        <option>Loading...</option>
                      ) : (
                        (rateCardsData?.rateCards || []).map((rc) => (
                          <option key={rc.id} value={rc.id}>
                            {rc.name} ({rc.currency}){rc.isDefault ? ' - Default' : ''}
                          </option>
                        ))
                      )}
                    </select>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="label">XV Architect Oversight %</label>
                      <input
                        type="number"
                        min={0}
                        max={100}
                        value={projectFormData.architectOversightPct}
                        onChange={(e) => handleProjectFormChange('architectOversightPct', parseInt(e.target.value))}
                        className="input mt-1 w-full"
                      />
                    </div>
                    <div>
                      <label className="label">XV Project Management %</label>
                      <input
                        type="number"
                        min={0}
                        max={100}
                        value={projectFormData.pmPct}
                        onChange={(e) => handleProjectFormChange('pmPct', parseInt(e.target.value))}
                        className="input mt-1 w-full"
                      />
                    </div>
                    <div>
                      <label className="label">BC Architect Oversight %</label>
                      <input
                        type="number"
                        min={0}
                        max={100}
                        value={projectFormData.bcArchitectOversightPct}
                        onChange={(e) => handleProjectFormChange('bcArchitectOversightPct', parseInt(e.target.value))}
                        className="input mt-1 w-full"
                      />
                    </div>
                    <div>
                      <label className="label">BC Project Management %</label>
                      <input
                        type="number"
                        min={0}
                        max={100}
                        value={projectFormData.bcPmPct}
                        onChange={(e) => handleProjectFormChange('bcPmPct', parseInt(e.target.value))}
                        className="input mt-1 w-full"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Scoping Progress Section */}
              <div className="settings-section">
                <div className="settings-section-header">
                  <Target className="settings-section-icon" />
                  <h3 className="settings-section-title">Scoping Progress</h3>
                </div>
                <div className="card-form">
                  <p className="text-xs md:text-sm text-gray-500 mb-3 md:mb-4">
                    Track the scoping stages for this project from partner briefing to PO sent.
                  </p>
                  <ProjectScopingTimeline
                    projectId={id!}
                    currentStage={project.currentScopingStage}
                    stages={project.scopingStages || []}
                    onStageUpdate={() => {
                      // Refetch project data
                    }}
                  />
                </div>
              </div>

              {/* Health Status Section */}
              <div className="settings-section">
                <div className="settings-section-header">
                  <HeartPulse className="settings-section-icon" />
                  <h3 className="settings-section-title">Project Health</h3>
                </div>
                <div className="card-form space-y-4">
                  <p className="text-xs md:text-sm text-gray-500">
                    Set the current delivery readiness status for this project.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-3 items-start">
                    <div>
                      <label className="label">Status</label>
                      <select
                        value={projectFormData.healthStatus}
                        onChange={(e) => handleProjectFormChange('healthStatus', e.target.value as RagStatus)}
                        className="input mt-1 w-full"
                      >
                        <option value="na">Not Set</option>
                        <option value="green">🟢 On Track</option>
                        <option value="amber">🟡 At Risk</option>
                        <option value="red">🔴 Blocked</option>
                      </select>
                    </div>
                    <div>
                      <label className="label">Notes</label>
                      <input
                        type="text"
                        value={projectFormData.healthNotes}
                        onChange={(e) => handleProjectFormChange('healthNotes', e.target.value)}
                        placeholder="Optional — explain the current status"
                        className="input mt-1 w-full"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Team Section */}
              <div className="settings-section">
                <div className="settings-section-header">
                  <Users className="settings-section-icon" />
                  <h3 className="settings-section-title">Team</h3>
                </div>
                <div className="card-form space-y-4">
                  <p className="text-xs md:text-sm text-gray-500">
                    Override the opportunity's team leads at the project level. Leave blank to inherit.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="label">Account Director</label>
                      <p className="mt-1 text-sm font-medium text-gray-700 px-3 py-2 bg-gray-50 rounded-md border border-gray-200">
                        {project.opportunity?.accountDirectorName || '—'}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">Inherited from opportunity</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="label">Scoping Lead</label>
                      <UserPicker
                        userId={projectFormData.scopingLeadId}
                        userName={projectFormData.scopingLeadName}
                        onChange={({ userId, userName }) =>
                          setProjectFormData((prev) => ({
                            ...prev,
                            scopingLeadId: userId,
                            scopingLeadName: userName || '',
                          }))
                        }
                        placeholder={project.opportunity?.scopingLeadName || 'Not set'}
                        className="mt-1"
                      />
                      {!projectFormData.scopingLeadName && project.opportunity?.scopingLeadName && (
                        <p className="text-xs text-gray-400 mt-1">Inheriting: {project.opportunity.scopingLeadName}</p>
                      )}
                    </div>
                    <div>
                      <label className="label">Delivery Lead</label>
                      <UserPicker
                        userId={projectFormData.deliveryLeadId}
                        userName={projectFormData.deliveryLeadName}
                        onChange={({ userId, userName }) =>
                          setProjectFormData((prev) => ({
                            ...prev,
                            deliveryLeadId: userId,
                            deliveryLeadName: userName || '',
                          }))
                        }
                        placeholder={project.opportunity?.deliveryLeadName || 'Not set'}
                        className="mt-1"
                      />
                      {!projectFormData.deliveryLeadName && project.opportunity?.deliveryLeadName && (
                        <p className="text-xs text-gray-400 mt-1">Inheriting: {project.opportunity.deliveryLeadName}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Mobilisation Stages Section */}
              <div className="settings-section">
                <div className="settings-section-header">
                  <Rocket className="settings-section-icon" />
                  <h3 className="settings-section-title">Delivery Readiness</h3>
                </div>
                <div className="card-form">
                  <p className="text-xs md:text-sm text-gray-500 mb-3 md:mb-4">
                    Track post-sale mobilisation progress through the 4 readiness stages.
                  </p>
                  <ProjectMobilisationTimeline
                    projectId={id!}
                    stages={project.mobilisationStages || []}
                  />
                </div>
              </div>
            </div>
          )}

          {activeTab === 'delivery' && (
            <DeliveryPlanTab
              workstreams={workstreams}
              selectedCapabilities={selectedCapabilities}
              guardrailValues={guardrailValues}
              onGuardrailChange={handleGuardrailChange}
              architectOversightPct={project.architectOversightPct}
              pmPct={project.pmPct}
              engagementStartDate={project.engagementStartDate}
              roles={rolesData?.roles || []}
              allocations={deliveryAllocations}
              capabilityStartWeeks={deliveryStartWeeks}
              onAllocationsChange={(newAllocations) => {
                setDeliveryAllocations(newAllocations);
                setHasDeliveryChanges(true);
                setHasChanges(true);
              }}
              onStartWeeksChange={(newStartWeeks) => {
                setDeliveryStartWeeks(newStartWeeks);
                setHasDeliveryChanges(true);
                setHasChanges(true);
              }}
              projectId={id!}
              enabledMultiplierIds={
                project.generalMultipliers
                  ?.filter((m) => m.isEnabled)
                  .map((m) => m.multiplierId) || []
              }
              daysPerWeek={projectFormData.daysPerWeek}
              savedDeliveryPlan={deliveryPlanData || null}
              deliveryProfileId={project.deliveryProfileId ?? null}
            />
          )}

          {activeTab === 'summary' && (
            <div className="space-y-6">
              {/* Existing Project Summary Content */}
              <ProjectSummaryTab
                projectId={id!}
                projectName={project.name}
                workstreams={workstreams}
                selectedCapabilities={selectedCapabilities}
                guardrailValues={guardrailValues}
                currency={project.currency || 'GBP'}
                architectOversightPct={project.architectOversightPct}
                pmPct={project.pmPct}
                bcArchitectOversightPct={project.bcArchitectOversightPct}
                bcPmPct={project.bcPmPct}
                daysPerWeek={project.daysPerWeek}
                entitlementValue={project.opportunity?.unscopedValue ?? 0}
                totalCost={project.totalCost}
                rateCardEntries={rateCardData?.entries || []}
              />
            </div>
          )}

          {activeTab === 'timeline' && (
            <GanttTimeline
              workstreams={workstreams}
              selectedCapabilities={selectedCapabilities}
              guardrailValues={guardrailValues}
              daysPerWeek={project.daysPerWeek}
            />
          )}

          {activeTab === 'forecasting' && (
            <ForecastEditor
              projectId={id!}
              currency={project.currency || 'GBP'}
              engagementStartDate={project.engagementStartDate}
            />
          )}

          {activeTab === 'history' && (
            <VersionHistoryTab projectId={id!} version={project.version} />
          )}
        </div>
      </div>

      {/* Pricing Summary Footer */}
      <div className="border-t border-gray-200 bg-gray-50 px-4 md:px-6 py-3">
        <div className="flex flex-wrap items-center justify-between gap-2 md:gap-4 text-sm">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 md:gap-x-6">
            <div>
              <span className="text-gray-500">Base Days:</span>
              <span className="ml-1 md:ml-2 font-medium text-xtravirt-dark">
                {pricingSummary.baseDays.toFixed(1)}
              </span>
            </div>
            <div>
              <span className="text-gray-500">Adjusted:</span>
              <span className="ml-1 md:ml-2 font-medium text-xtravirt-dark">
                {pricingSummary.adjustedDays.toFixed(1)}
              </span>
            </div>
            <div>
              <span className="text-gray-500">+Architect ({(project.architectOversightPct / 100).toFixed(0)}%):</span>
              <span className="ml-1 md:ml-2 font-medium text-xtravirt-dark">
                {pricingSummary.architectOversight.toFixed(1)}
              </span>
            </div>
            <div>
              <span className="text-gray-500">+PM ({(project.pmPct / 100).toFixed(0)}%):</span>
              <span className="ml-1 md:ml-2 font-medium text-xtravirt-dark">
                {pricingSummary.pm.toFixed(1)}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3 md:gap-4">
            <div>
              <span className="text-gray-600 font-medium">Total Days:</span>
              <span className="ml-1 md:ml-2 text-lg font-bold text-xtravirt-dark">
                {pricingSummary.totalDays.toFixed(1)}
              </span>
            </div>
            <div>
              <span className="text-gray-600 font-medium">Services Cost:</span>
              <span className="ml-1 md:ml-2 text-lg font-bold text-green-700">
                {formatCurrency(project.totalCost || pricingSummary.totalCostCents, project.currency || 'GBP')}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Mark As Lost Modal */}
      {showMarkLostModal && (
        <MarkLostModal
          projectId={id!}
          onClose={() => setShowMarkLostModal(false)}
          onSuccess={() => setShowMarkLostModal(false)}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowDeleteConfirm(false)}
          />
          <div className="relative z-10 bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="font-heading text-lg font-semibold text-xtravirt-dark mb-2">
              Delete Project
            </h3>
            <p className="text-gray-600 mb-4">
              Are you sure you want to delete <strong>{project.name}</strong>? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="btn-secondary"
                disabled={deleteProject.isPending}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleteProject.isPending}
                className="btn-primary bg-red-600 hover:bg-red-700 focus:ring-red-500"
              >
                {deleteProject.isPending ? 'Deleting...' : 'Delete Project'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============ Delivery Plan Tab ============

const NUM_WEEKS = 44;
const DAYS_PER_WEEK = 5;

// Phase colours are static; skills/roles colours come from DB via hooks
const PHASE_COLOURS = SHARED_PHASE_COLOURS;

interface ActivityAllocation {
  baseDaysOverride?: number; // days (not centdays)
  startWeekOverride?: number; // manual start week override
  weekAllocations: Record<number, number>; // week -> days
  roleOverride?: string;
  isExcluded?: boolean; // Exclude from totals
  notes?: string; // Activity notes
}

interface DeliveryPlanTabProps {
  workstreams: VcfWorkstream[];
  selectedCapabilities: Set<string>;
  guardrailValues: Record<string, string>;
  onGuardrailChange: (guardrailId: string, value: string) => void;
  architectOversightPct?: number;
  pmPct?: number;
  engagementStartDate?: string | null;
  roles: Array<{ id: string; name: string; colorBg?: string | null; colorText?: string | null }>;
  // Parent-controlled state
  allocations: Record<string, ActivityAllocation>;
  capabilityStartWeeks: Record<string, number>;
  onAllocationsChange: (allocations: Record<string, ActivityAllocation>) => void;
  onStartWeeksChange: (startWeeks: Record<string, number>) => void;
  // General multipliers
  projectId: string;
  enabledMultiplierIds: string[];
  daysPerWeek: number;
  savedDeliveryPlan: { allocations: Array<{ activityId: string; week: number; days: number }>; activityOverrides: Array<{ activityId: string; overrideDays: number | null; overrideRole: string | null }> } | null;
  // The Service Package (delivery profile) assigned to this project, if any.
  // Drives the effective-plan resolver: base days come from the package
  // overlay on top of the Services Framework baseline.
  deliveryProfileId: string | null;
}

// Resource Utilisation Panel — shows per-resource loading heat map
function ResourceUtilisationPanel({
  selectedCapabilitiesData,
  allocations,
  daysPerWeek,
  numWeeks,
}: {
  selectedCapabilitiesData: Array<{ capability: VcfCapability }>;
  allocations: Record<string, ActivityAllocation>;
  daysPerWeek: number;
  numWeeks: number;
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  const resourceData = useMemo(() => {
    const resourceWeeks: Record<string, Record<number, number>> = {};

    for (const { capability } of selectedCapabilitiesData) {
      for (const act of capability.activities || []) {
        const alloc = allocations[act.id];
        if (!alloc || alloc.isExcluded) continue;
        const role = alloc.roleOverride ?? act.role;
        const key = `${role} / ${act.skill || 'General'}`;
        if (!resourceWeeks[key]) resourceWeeks[key] = {};

        for (const [week, days] of Object.entries(alloc.weekAllocations)) {
          const w = Number(week);
          resourceWeeks[key][w] = (resourceWeeks[key][w] || 0) + days;
        }
      }
    }

    // Find max week with allocations
    let maxWeek = 0;
    for (const weeks of Object.values(resourceWeeks)) {
      for (const w of Object.keys(weeks).map(Number)) {
        if (w > maxWeek) maxWeek = w;
      }
    }

    return { resourceWeeks, maxWeek: Math.min(maxWeek + 2, numWeeks) };
  }, [selectedCapabilitiesData, allocations, numWeeks]);

  const resources = Object.keys(resourceData.resourceWeeks).sort();
  if (resources.length === 0) return null;

  return (
    <div className="card overflow-hidden !p-0 mt-4">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50"
      >
        <span className="flex items-center gap-2">
          <BarChart3 className="h-4 w-4 text-gray-400" />
          Resource Utilisation ({resources.length} resources)
        </span>
        {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
      </button>
      {isExpanded && (
        <div className="overflow-x-auto border-t border-gray-200">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-3 py-2 text-left font-medium text-gray-500 min-w-[200px]">Resource</th>
                {Array.from({ length: resourceData.maxWeek }, (_, i) => (
                  <th key={i} className="px-1 py-2 text-center font-medium text-gray-400 w-10">W{i + 1}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {resources.map((resource) => (
                <tr key={resource} className="border-t border-gray-100">
                  <td className="px-3 py-1.5 font-medium text-gray-700 whitespace-nowrap">{resource}</td>
                  {Array.from({ length: resourceData.maxWeek }, (_, i) => {
                    const week = i + 1;
                    const days = resourceData.resourceWeeks[resource][week] || 0;
                    const pct = daysPerWeek > 0 ? (days / daysPerWeek) * 100 : 0;
                    const bg = pct === 0 ? '' : pct <= 80 ? 'bg-green-100 text-green-800' : pct <= 100 ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800';
                    return (
                      <td key={i} className={cn('px-1 py-1.5 text-center tabular-nums', bg)}>
                        {pct > 0 ? `${Math.round(pct)}%` : ''}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function DeliveryPlanTab({
  workstreams,
  selectedCapabilities,
  guardrailValues,
  onGuardrailChange,
  architectOversightPct = 1000,
  pmPct = 2000,
  engagementStartDate,
  roles,
  allocations,
  capabilityStartWeeks,
  onAllocationsChange,
  onStartWeeksChange,
  projectId,
  enabledMultiplierIds,
  daysPerWeek: propDaysPerWeek,
  savedDeliveryPlan,
  deliveryProfileId,
}: DeliveryPlanTabProps) {
  // State for collapsible multipliers panel
  const [multipliersExpanded, setMultipliersExpanded] = useState(false);

  // Skills data for colour lookup (cached at hook level)
  const { data: skillsData } = useSkills();
  const skillsList = skillsData?.skills || [];

  // Effective delivery plan: Services Framework baseline merged with
  // the optional Service Package overlay. Projects inherit base days
  // from this when they don't have their own override in place.
  // Falls back to each activity's raw baseDays while loading.
  const { data: effectivePlan } = useEffectiveDeliveryPlan(deliveryProfileId);
  const effectiveBaseDaysMap = useMemo(() => {
    const map = new Map<string, number>(); // activityId -> centdays
    for (const act of effectivePlan?.activities || []) {
      map.set(act.activityId, act.baseDays);
    }
    return map;
  }, [effectivePlan]);
  // Wrapper functions to handle both direct values and updater functions
  const setAllocations = useCallback((
    valueOrUpdater: Record<string, ActivityAllocation> | ((prev: Record<string, ActivityAllocation>) => Record<string, ActivityAllocation>)
  ) => {
    if (!isAutoGenerating.current) {
      undoStackRef.current.push(allocations);
      forceRender(n => n + 1);
    }
    if (typeof valueOrUpdater === 'function') {
      onAllocationsChange(valueOrUpdater(allocations));
    } else {
      onAllocationsChange(valueOrUpdater);
    }
  }, [allocations, onAllocationsChange]);

  // Undo/redo stack for allocation changes
  const undoStackRef = useRef(createUndoStack<Record<string, ActivityAllocation>>(50));
  const [, forceRender] = useState(0);
  const isAutoGenerating = useRef(false);

  const handleUndo = useCallback(() => {
    const prev = undoStackRef.current.undo();
    if (prev) {
      onAllocationsChange(prev);
      forceRender(n => n + 1);
    }
  }, [onAllocationsChange]);

  const handleRedo = useCallback(() => {
    const next = undoStackRef.current.redo();
    if (next) {
      onAllocationsChange(next);
      forceRender(n => n + 1);
    }
  }, [onAllocationsChange]);

  // Keyboard shortcuts for undo/redo
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'z') {
        e.preventDefault();
        if (e.shiftKey) {
          handleRedo();
        } else {
          handleUndo();
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [handleUndo, handleRedo]);

  const setCapabilityStartWeeks = useCallback((
    valueOrUpdater: Record<string, number> | ((prev: Record<string, number>) => Record<string, number>)
  ) => {
    if (typeof valueOrUpdater === 'function') {
      onStartWeeksChange(valueOrUpdater(capabilityStartWeeks));
    } else {
      onStartWeeksChange(valueOrUpdater);
    }
  }, [capabilityStartWeeks, onStartWeeksChange]);

  // UI state (not persisted)
  const [expandedCapabilities, setExpandedCapabilities] = useState<Set<string>>(new Set());

  // Collect all guardrails from selected capabilities
  const relevantGuardrails = useMemo(() => {
    const guardrails: Array<{
      guardrail: { id: string; name: string; code: string; baseline: string | null; multiplier: string | null; baselineValue: number | null; multiplierFactor: number | null; multiplierStep: number | null; multiplierLabel: string | null; optionFactors: Record<string, number> | null; inputType: string; options: string[] | null; defaultValue: string | null; minValue: number | null; maxValue: number | null };
      capabilityName: string;
      capabilityId: string;
      workstreamCode: string;
    }> = [];

    for (const ws of workstreams) {
      for (const cap of ws.capabilities || []) {
        if (selectedCapabilities.has(cap.id)) {
          for (const gr of cap.guardrails || []) {
            guardrails.push({
              guardrail: gr,
              capabilityName: cap.name,
              capabilityId: cap.id,
              workstreamCode: ws.code,
            });
          }
        }
      }
    }

    return guardrails;
  }, [workstreams, selectedCapabilities]);

  // Calculate guardrail factor for a capability using structured fields
  const calculateGuardrailFactor = useCallback((capabilityId: string): number => {
    let factor = 1;
    for (const { guardrail, capabilityId: gCapId } of relevantGuardrails) {
      if (gCapId !== capabilityId) continue;
      const value = guardrailValues[guardrail.id];
      if (!value) continue;
      const mf = guardrail.multiplierFactor;
      if (mf == null || mf === 0) continue;
      const step = guardrail.multiplierStep || 1;
      const bv = guardrail.baselineValue ?? 0;

      switch (guardrail.inputType) {
        case 'number': {
          const current = parseFloat(value);
          if (!isNaN(current)) {
            factor += mf * Math.max(0, Math.floor((current - bv) / step));
          }
          break;
        }
        case 'boolean':
          if (value === 'true') factor += mf;
          break;
        case 'select':
          if (guardrail.optionFactors) {
            factor += guardrail.optionFactors[value] ?? 0;
          }
          break;
      }
    }
    return factor;
  }, [relevantGuardrails, guardrailValues]);

  // State for adding custom activities
  const [showAddActivity, setShowAddActivity] = useState<string | null>(null);
  const [newActivityName, setNewActivityName] = useState('');
  const [newActivityRole, setNewActivityRole] = useState('Consultant');
  const [newActivityDays, setNewActivityDays] = useState('1.0');
  const [customActivities, setCustomActivities] = useState<Record<string, Array<{
    id: string;
    name: string;
    role: string;
    baseDays: number;
    skill: string | null;
    sortOrder: number;
  }>>>({});

  // Helper to get week date based on engagement start date
  const getWeekDate = useCallback((weekNum: number): Date | null => {
    if (!engagementStartDate) return null;
    const start = new Date(engagementStartDate);
    const weekDate = new Date(start);
    weekDate.setDate(start.getDate() + (weekNum - 1) * 7);
    return weekDate;
  }, [engagementStartDate]);

  // Handle adding a new activity
  const handleAddActivity = useCallback((capabilityId: string) => {
    if (!newActivityName.trim()) return;

    const newActivity = {
      id: `custom_${Date.now()}`,
      name: newActivityName.trim(),
      role: newActivityRole,
      baseDays: Math.round(parseFloat(newActivityDays) * 100),
      skill: null,
      sortOrder: 999,
    };

    setCustomActivities(prev => ({
      ...prev,
      [capabilityId]: [...(prev[capabilityId] || []), newActivity]
    }));

    setNewActivityName('');
    setNewActivityDays('1.0');
    setShowAddActivity(null);
  }, [newActivityName, newActivityRole, newActivityDays]);

  // Get selected capabilities with their activities
  const selectedCapabilitiesData = useMemo(() => {
    const result: Array<{
      workstream: VcfWorkstream;
      capability: VcfCapability;
    }> = [];

    for (const ws of workstreams) {
      for (const cap of ws.capabilities || []) {
        if (selectedCapabilities.has(cap.id)) {
          result.push({ workstream: ws, capability: cap });
        }
      }
    }

    return result;
  }, [workstreams, selectedCapabilities]);

  // Auto-expand all selected capabilities on first load
  useEffect(() => {
    if (selectedCapabilitiesData.length > 0 && expandedCapabilities.size === 0) {
      setExpandedCapabilities(new Set(selectedCapabilitiesData.map(({ capability }) => capability.id)));
    }
  }, [selectedCapabilitiesData, expandedCapabilities.size]);

  // Track which capabilities have been auto-generated
  const [autoGeneratedCaps, setAutoGeneratedCaps] = useState<Set<string>>(new Set());

  // Toggle capability expansion
  const toggleCapability = useCallback((capId: string) => {
    setExpandedCapabilities((prev) => {
      const next = new Set(prev);
      if (next.has(capId)) {
        next.delete(capId);
      } else {
        next.add(capId);
      }
      return next;
    });
  }, []);

  // Get activity with allocations and guardrail adjustments
  const getActivityWithAllocation = useCallback(
    (activity: VcfActivity, capabilityId: string): VcfActivity & { allocation: ActivityAllocation; allocatedDays: number; isModified: boolean; adjustedDays: number; originalAdjustedDays: number; guardrailFactor: number; isExcluded: boolean; notes: string } => {
      const allocation = allocations[activity.id] || { weekAllocations: {} };
      const allocatedDays = Object.values(allocation.weekAllocations).reduce((sum, d) => sum + d, 0);
      const isModified = allocation.baseDaysOverride !== undefined || allocation.roleOverride !== undefined || !!allocation.isExcluded || !!allocation.notes;
      // baseDaysOverride is in days, activity.baseDays is in centdays
      const baseDays = allocation.baseDaysOverride !== undefined ? allocation.baseDaysOverride : (activity.baseDays || 0) / 100;
      const guardrailFactor = calculateGuardrailFactor(capabilityId);
      const adjustedDays = baseDays * guardrailFactor;
      const originalAdjustedDays = ((activity.baseDays || 0) / 100) * guardrailFactor;

      return {
        ...activity,
        baseDays: Math.round(baseDays * 100),
        role: allocation.roleOverride ?? activity.role,
        allocation,
        allocatedDays,
        isModified,
        adjustedDays,
        originalAdjustedDays,
        guardrailFactor,
        isExcluded: allocation.isExcluded ?? false,
        notes: allocation.notes ?? '',
      };
    },
    [allocations, calculateGuardrailFactor]
  );

  // Update activity days
  const updateActivityDays = useCallback((activityId: string, baseDays: number) => {
    setAllocations((prev) => ({
      ...prev,
      [activityId]: {
        ...prev[activityId],
        weekAllocations: prev[activityId]?.weekAllocations || {},
        baseDaysOverride: baseDays,
      },
    }));
  }, [setAllocations]);

  // Update activity start week — shifts existing allocations and cascades to dependents
  const updateActivityStartWeek = useCallback((activityId: string, newStartWeek: number, capability: VcfCapability) => {
    setAllocations((prev) => {
      const updated = { ...prev };
      const current = updated[activityId] || { weekAllocations: {} };
      const currentWeeks = Object.keys(current.weekAllocations).map(Number).sort((a, b) => a - b);
      const currentStart = currentWeeks[0] || newStartWeek;
      const offset = newStartWeek - currentStart;

      if (offset === 0) return prev;

      // Shift this activity's allocations
      const shiftedWeeks: Record<number, number> = {};
      for (const [week, days] of Object.entries(current.weekAllocations)) {
        const newWeek = Number(week) + offset;
        if (newWeek >= 1 && newWeek <= NUM_WEEKS) {
          shiftedWeeks[newWeek] = days;
        }
      }
      updated[activityId] = { ...current, weekAllocations: shiftedWeeks, startWeekOverride: newStartWeek };

      // Cascade to dependents: shift any activity that depends on this one
      const activities = capability.activities || [];
      const shifted = new Set<string>([activityId]);

      // Find last week of shifted activity
      const shiftedEnd = Math.max(...Object.keys(shiftedWeeks).map(Number), newStartWeek);

      for (const act of activities) {
        if (shifted.has(act.id)) continue;
        const deps = act.dependencies || [];
        const dependsOnShifted = deps.some(d => shifted.has(d.predecessorId) && d.dependencyType === 'FS');
        if (!dependsOnShifted) continue;

        const actAlloc = updated[act.id] || { weekAllocations: {} };
        const actWeeks = Object.keys(actAlloc.weekAllocations).map(Number).sort((a, b) => a - b);
        const actStart = actWeeks[0];

        if (actStart !== undefined && actStart < shiftedEnd + 1) {
          const actOffset = (shiftedEnd + 1) - actStart;
          const newActWeeks: Record<number, number> = {};
          for (const [w, d] of Object.entries(actAlloc.weekAllocations)) {
            const nw = Number(w) + actOffset;
            if (nw >= 1 && nw <= NUM_WEEKS) newActWeeks[nw] = d;
          }
          updated[act.id] = { ...actAlloc, weekAllocations: newActWeeks };
          shifted.add(act.id);
        }
      }

      return updated;
    });
  }, [setAllocations]);

  // Update week allocation
  const updateWeekAllocation = useCallback((activityId: string, week: number, days: number) => {
    setAllocations((prev) => {
      const current = prev[activityId] || { weekAllocations: {} };
      const weekAllocations = { ...current.weekAllocations };

      if (days > 0) {
        weekAllocations[week] = days;
      } else {
        delete weekAllocations[week];
      }

      return {
        ...prev,
        [activityId]: {
          ...current,
          weekAllocations,
        },
      };
    });
  }, [setAllocations]);

  // Reset activity to defaults
  const resetActivity = useCallback((activityId: string) => {
    setAllocations((prev) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [activityId]: _, ...rest } = prev;
      return rest;
    });
  }, [setAllocations]);

  // Toggle activity exclusion
  const toggleActivityExcluded = useCallback((activityId: string) => {
    setAllocations((prev) => ({
      ...prev,
      [activityId]: {
        ...prev[activityId],
        weekAllocations: prev[activityId]?.weekAllocations || {},
        isExcluded: !prev[activityId]?.isExcluded,
      },
    }));
  }, [setAllocations]);

  // Update activity notes
  const updateActivityNotes = useCallback((activityId: string, notes: string) => {
    setAllocations((prev) => ({
      ...prev,
      [activityId]: {
        ...prev[activityId],
        weekAllocations: prev[activityId]?.weekAllocations || {},
        notes: notes || undefined,
      },
    }));
  }, [setAllocations]);

  // Track which activities have notes expanded
  // Shift an activity's allocations left or right by one week
  const shiftActivityWeeks = useCallback((activityId: string, direction: -1 | 1) => {
    setAllocations((prev) => {
      const current = prev[activityId];
      if (!current) return prev;
      const shifted: Record<number, number> = {};
      for (const [week, days] of Object.entries(current.weekAllocations)) {
        const newWeek = Number(week) + direction;
        if (newWeek >= 1 && newWeek <= NUM_WEEKS) {
          shifted[newWeek] = days;
        }
      }
      return { ...prev, [activityId]: { ...current, weekAllocations: shifted } };
    });
  }, [setAllocations]);

  const [expandedNotes, setExpandedNotes] = useState<Set<string>>(new Set());
  const [criticalPathIds, setCriticalPathIds] = useState<Set<string>>(new Set());

  const toggleNotesExpanded = useCallback((activityId: string) => {
    setExpandedNotes((prev) => {
      const next = new Set(prev);
      if (next.has(activityId)) {
        next.delete(activityId);
      } else {
        next.add(activityId);
      }
      return next;
    });
  }, []);

  // Generate allocations for a capability (can merge with existing or reset)
  // Uses guardrail factor to calculate adjusted days for allocations.
  //
  // "Reset to defaults" uses the effective plan's base days — that's the
  // Services Framework baseline with any Service Package overlay applied.
  // If the effective plan hasn't loaded yet (or the activity doesn't
  // appear in it), it falls back to the activity's raw framework
  // baseDays so Generate keeps working during the fetch.
  const generateCapabilityAllocations = useCallback((
    capability: VcfCapability,
    startWeek: number,
    currentAllocations: Record<string, ActivityAllocation>,
    resetToDefaults = false
  ): Record<string, ActivityAllocation> => {
    const activities = capability.activities || [];
    const guardrailFactor = calculateGuardrailFactor(capability.id);
    const dpw = propDaysPerWeek || DAYS_PER_WEEK;

    // Build activity list with adjusted days
    const genActivities = activities.map((act) => {
      const override = currentAllocations[act.id]?.baseDaysOverride;
      const effectiveBaseDays =
        (effectiveBaseDaysMap.get(act.id) ?? act.baseDays) / 100;
      const rawBaseDays = resetToDefaults
        ? effectiveBaseDays
        : (override !== undefined ? override : effectiveBaseDays);
      return {
        id: act.id,
        role: resetToDefaults ? act.role : (currentAllocations[act.id]?.roleOverride ?? act.role),
        skill: act.skill || '',
        adjustedDays: rawBaseDays * guardrailFactor,
        phase: act.activityPhase,
        sortOrder: act.sortOrder,
        predecessors: act.dependencies?.map(d => ({
          predecessorId: d.predecessorId,
          type: d.dependencyType,
          lagDays: d.lagDays,
        })),
      };
    });

    const scheduleResult = generateWeekAllocations(genActivities, startWeek, dpw, NUM_WEEKS);
    const weekAllocs = scheduleResult.allocations;
    setCriticalPathIds(scheduleResult.criticalPath);

    const newAllocations: Record<string, ActivityAllocation> = { ...currentAllocations };
    for (const act of activities) {
      if (resetToDefaults) {
        newAllocations[act.id] = { weekAllocations: weekAllocs[act.id] || {} };
      } else {
        newAllocations[act.id] = {
          ...newAllocations[act.id],
          weekAllocations: weekAllocs[act.id] || {},
        };
      }
    }
    return newAllocations;
  }, [calculateGuardrailFactor, propDaysPerWeek, effectiveBaseDaysMap]);

  // Generate: auto-schedule from current LOE values
  const generateCapability = useCallback((capability: VcfCapability) => {
    const startWeek = capabilityStartWeeks[capability.id] || 1;
    const newAllocations = generateCapabilityAllocations(capability, startWeek, allocations, false);
    setAllocations(newAllocations);
  }, [generateCapabilityAllocations, capabilityStartWeeks, allocations]);

  // Reset: restore delivery profile's saved allocations
  const resetCapabilityToProfile = useCallback((capability: VcfCapability) => {
    const activities = capability.activities || [];
    const newAllocations: Record<string, ActivityAllocation> = { ...allocations };

    for (const act of activities) {
      // Find this activity's saved allocation from the loaded delivery plan data
      const savedWeekAllocs: Record<number, number> = {};
      if (savedDeliveryPlan) {
        for (const a of savedDeliveryPlan.allocations) {
          if (a.activityId === act.id) {
            savedWeekAllocs[a.week] = a.days;
          }
        }
      }

      // Find saved override
      const savedOverride = savedDeliveryPlan?.activityOverrides?.find(o => o.activityId === act.id);

      newAllocations[act.id] = {
        weekAllocations: savedWeekAllocs,
        baseDaysOverride: savedOverride?.overrideDays ?? undefined,
        roleOverride: savedOverride?.overrideRole ?? undefined,
      };
    }

    setAllocations(newAllocations);
  }, [allocations, savedDeliveryPlan]);

  // Track previous start weeks to detect changes (must be defined before the effects that use it)
  const prevStartWeeksRef = useRef<Record<string, number>>({});

  // Auto-generate allocations for newly selected capabilities
  useEffect(() => {
    const newCapsToGenerate = selectedCapabilitiesData.filter(
      ({ capability }) => !autoGeneratedCaps.has(capability.id)
    );

    if (newCapsToGenerate.length > 0) {
      isAutoGenerating.current = true;
      let newAllocations = { ...allocations };
      const newAutoGenerated = new Set(autoGeneratedCaps);
      const newPrevStartWeeks = { ...prevStartWeeksRef.current };

      newCapsToGenerate.forEach(({ capability }) => {
        const startWeek = capabilityStartWeeks[capability.id] || 1;
        newAllocations = generateCapabilityAllocations(capability, startWeek, newAllocations, false);
        newAutoGenerated.add(capability.id);
        newPrevStartWeeks[capability.id] = startWeek;
      });

      setAllocations(newAllocations);
      setAutoGeneratedCaps(newAutoGenerated);
      prevStartWeeksRef.current = newPrevStartWeeks;
      isAutoGenerating.current = false;
    }
  }, [selectedCapabilitiesData, autoGeneratedCaps, capabilityStartWeeks, allocations, generateCapabilityAllocations]);

  // Shift all allocations for a capability by a week offset
  const shiftCapabilityAllocations = useCallback((
    capability: VcfCapability,
    weekOffset: number,
    currentAllocations: Record<string, ActivityAllocation>
  ): Record<string, ActivityAllocation> => {
    const activities = capability.activities || [];
    const newAllocations = { ...currentAllocations };

    activities.forEach((act) => {
      const currentAlloc = currentAllocations[act.id];
      if (currentAlloc?.weekAllocations) {
        const shiftedWeekAllocations: Record<number, number> = {};

        Object.entries(currentAlloc.weekAllocations).forEach(([week, days]) => {
          const newWeek = Number(week) + weekOffset;
          // Only keep allocations within valid week range
          if (newWeek >= 1 && newWeek <= NUM_WEEKS) {
            shiftedWeekAllocations[newWeek] = days;
          }
        });

        newAllocations[act.id] = {
          ...currentAlloc,
          weekAllocations: shiftedWeekAllocations,
        };
      }
    });

    return newAllocations;
  }, []);

  // Shift allocations when start week changes
  useEffect(() => {
    const prevStartWeeks = prevStartWeeksRef.current;

    // Find capabilities whose start week actually changed
    const changedCaps: Array<{ capability: VcfCapability; weekOffset: number }> = [];

    selectedCapabilitiesData.forEach(({ capability }) => {
      const currentStartWeek = capabilityStartWeeks[capability.id];
      const prevStartWeek = prevStartWeeks[capability.id];

      // Only shift if start week changed and we have a previous value to compare
      if (currentStartWeek !== undefined &&
          prevStartWeek !== undefined &&
          currentStartWeek !== prevStartWeek) {
        const weekOffset = currentStartWeek - prevStartWeek;
        changedCaps.push({ capability, weekOffset });
      }
    });

    if (changedCaps.length > 0) {
      let newAllocations = { ...allocations };
      changedCaps.forEach(({ capability, weekOffset }) => {
        newAllocations = shiftCapabilityAllocations(capability, weekOffset, newAllocations);
      });
      setAllocations(newAllocations);
    }

    // Update ref with current values
    prevStartWeeksRef.current = { ...capabilityStartWeeks };
  }, [capabilityStartWeeks, selectedCapabilitiesData, allocations, shiftCapabilityAllocations]);

  // Calculate week totals for a capability
  const getCapabilityWeekTotals = useCallback(
    (capability: VcfCapability): Record<number, number> => {
      const weekTotals: Record<number, number> = {};

      (capability.activities || []).forEach((act) => {
        const allocation = allocations[act.id];
        // Skip excluded activities
        if (allocation?.isExcluded) return;
        const weekAllocations = allocation?.weekAllocations || {};
        Object.entries(weekAllocations).forEach(([week, days]) => {
          weekTotals[Number(week)] = (weekTotals[Number(week)] || 0) + days;
        });
      });

      return weekTotals;
    },
    [allocations]
  );

  // Calculate per-resource week totals for over-allocation checking
  const getCapabilityWeekResourceTotals = useCallback(
    (capability: VcfCapability): Record<number, Record<string, number>> => {
      const acts = (capability.activities || []).map(act => ({
        id: act.id,
        role: allocations[act.id]?.roleOverride ?? act.role,
        skill: act.skill || '',
        isExcluded: allocations[act.id]?.isExcluded,
      }));
      return computeWeekResourceTotals(acts, allocations);
    },
    [allocations]
  );

  // Calculate totals (using adjusted days with guardrail factors)
  const totals = useMemo(() => {
    let totalBaseDays = 0;
    let totalAdjustedDays = 0;
    let totalAllocatedDays = 0;
    let totalActivities = 0;
    let excludedActivities = 0;

    for (const { capability } of selectedCapabilitiesData) {
      for (const act of capability.activities || []) {
        const withAlloc = getActivityWithAllocation(act, capability.id);
        // Skip excluded activities in totals
        if (withAlloc.isExcluded) {
          excludedActivities++;
          continue;
        }
        totalBaseDays += withAlloc.baseDays / 100;
        totalAdjustedDays += withAlloc.adjustedDays;
        totalAllocatedDays += withAlloc.allocatedDays;
        totalActivities++;
      }
    }

    return { totalBaseDays, totalAdjustedDays, totalAllocatedDays, totalActivities, excludedActivities };
  }, [selectedCapabilitiesData, getActivityWithAllocation]);

  if (selectedCapabilitiesData.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <FileText className="h-12 w-12 mx-auto text-gray-300 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No capabilities selected</h3>
        <p className="text-sm">Select capabilities from the Capabilities tab to see the delivery plan.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* General Multipliers Section */}
      <div className="card overflow-hidden !p-0">
        <button
          onClick={() => setMultipliersExpanded(!multipliersExpanded)}
          className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Percent className="h-5 w-5 text-gray-500" />
            <h3 className="font-semibold text-gray-900">General Multipliers</h3>
            <span className="text-sm text-gray-500">
              ({enabledMultiplierIds.length} enabled)
            </span>
          </div>
          <ChevronDown className={cn("h-4 w-4 text-gray-500 transition-transform", multipliersExpanded && "rotate-180")} />
        </button>
        {multipliersExpanded && (
          <div className="p-4 border-t border-gray-200">
            <GeneralMultipliersPanel
              projectId={projectId}
              enabledMultiplierIds={enabledMultiplierIds}
            />
          </div>
        )}
      </div>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-heading text-lg font-semibold text-xtravirt-dark">
            Delivery Plan
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Activities are auto-scheduled based on defaults. Change start week to shift allocations.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              if (!window.confirm('Generate All will auto-schedule week allocations from current LOE values for all capabilities. Continue?')) return;
              let newAllocations = { ...allocations };
              selectedCapabilitiesData.forEach(({ capability }) => {
                const startWeek = capabilityStartWeeks[capability.id] || 1;
                newAllocations = generateCapabilityAllocations(capability, startWeek, newAllocations, false);
              });
              setAllocations(newAllocations);
            }}
            className="btn-secondary btn-sm"
          >
            Generate All
          </button>
          <button
            onClick={() => {
              if (!window.confirm('Reset All will restore all capabilities to their last saved allocations. Continue?')) return;
              selectedCapabilitiesData.forEach(({ capability }) => {
                resetCapabilityToProfile(capability);
              });
            }}
            className="btn-secondary btn-sm"
          >
            Reset All
          </button>
          <div className="flex items-center gap-1 ml-2 border-l border-gray-200 pl-2">
            <button
              onClick={handleUndo}
              disabled={!undoStackRef.current.canUndo}
              className="btn-secondary btn-sm px-2 disabled:opacity-30"
              title="Undo (Ctrl+Z)"
            >
              <RotateCcw className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={handleRedo}
              disabled={!undoStackRef.current.canRedo}
              className="btn-secondary btn-sm px-2 disabled:opacity-30"
              title="Redo (Ctrl+Shift+Z)"
            >
              <RotateCcw className="h-3.5 w-3.5 -scale-x-100" />
            </button>
          </div>
        </div>
      </div>

      {/* Capabilities with Activities */}
      {selectedCapabilitiesData.map(({ workstream, capability }) => {
        const isExpanded = expandedCapabilities.has(capability.id);
        // Merge custom activities with base activities
        const baseActivities = capability.activities || [];
        const custom = customActivities[capability.id] || [];
        const activities = [...baseActivities, ...custom] as VcfActivity[];
        const guardrailFactor = calculateGuardrailFactor(capability.id);
        const capBaseDays = activities.reduce((sum, a) => {
          const withAlloc = getActivityWithAllocation(a, capability.id);
          if (withAlloc.isExcluded) return sum;
          return sum + withAlloc.baseDays / 100;
        }, 0);
        const capAdjustedDays = activities.reduce((sum, a) => {
          const withAlloc = getActivityWithAllocation(a, capability.id);
          if (withAlloc.isExcluded) return sum;
          return sum + withAlloc.adjustedDays;
        }, 0);
        const capAllocatedDays = activities.reduce((sum, a) => {
          const withAlloc = getActivityWithAllocation(a, capability.id);
          if (withAlloc.isExcluded) return sum;
          return sum + withAlloc.allocatedDays;
        }, 0);
        const weekTotals = getCapabilityWeekTotals(capability);
        const weekResourceTotals = getCapabilityWeekResourceTotals(capability);
        const dpw = propDaysPerWeek || DAYS_PER_WEEK;
        const hasMismatch = Math.abs(capAllocatedDays - capAdjustedDays) > 0.01;

        return (
          <div
            key={capability.id}
            className="dp-capability-section card overflow-hidden !p-0"
          >
            {/* Capability Header */}
            <div
              className="dp-capability-header"
              onClick={() => toggleCapability(capability.id)}
            >
              <div className="flex items-center gap-3">
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-gray-400" />
                )}
                <div>
                  <div className="flex items-center gap-2">
                    <span className="dp-capability-code">
                      {capability.code}
                    </span>
                    <span className="dp-capability-name">
                      {capability.name}
                    </span>
                  </div>
                  <p className="dp-capability-meta">
                    {workstream.name} &middot; {activities.length} activities
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="dp-capability-start">
                  <label>Start:</label>
                  <select
                    value={capabilityStartWeeks[capability.id] || 1}
                    onChange={(e) => {
                      e.stopPropagation();
                      setCapabilityStartWeeks(prev => ({
                        ...prev,
                        [capability.id]: parseInt(e.target.value)
                      }));
                    }}
                    onClick={(e) => e.stopPropagation()}
                    className="dp-start-week-select"
                  >
                    {Array.from({ length: NUM_WEEKS }, (_, i) => (
                      <option key={i + 1} value={i + 1}>W{i + 1}</option>
                    ))}
                  </select>
                </div>
                <div className="text-right">
                  {guardrailFactor > 1 ? (
                    <>
                      <p className="dp-capability-days">
                        {capAdjustedDays.toFixed(1)} days
                        <span className="text-xs text-xtravirt-cyan ml-1">({guardrailFactor.toFixed(2)}x)</span>
                      </p>
                      <p className="text-xs text-gray-400">Base: {capBaseDays.toFixed(1)}d</p>
                    </>
                  ) : (
                    <p className="dp-capability-days">
                      {capBaseDays.toFixed(1)} days
                    </p>
                  )}
                  {hasMismatch && (
                    <p className={capAllocatedDays > capAdjustedDays ? 'dp-capability-alloc-warning' : 'dp-capability-alloc-pending'}>
                      {capAllocatedDays > capAdjustedDays
                        ? `+${(capAllocatedDays - capAdjustedDays).toFixed(1)} over`
                        : `${(capAdjustedDays - capAllocatedDays).toFixed(1)} to allocate`}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (window.confirm('Generate will auto-schedule week allocations from current LOE values. This overwrites existing week allocations. Continue?')) {
                        generateCapability(capability);
                      }
                    }}
                    className="btn-secondary btn-sm text-xs"
                    title="Auto-schedule weeks from LOE values"
                  >
                    Generate
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (window.confirm('Reset will restore week allocations to the last saved state. Any unsaved changes will be lost. Continue?')) {
                        resetCapabilityToProfile(capability);
                      }
                    }}
                    className="btn-secondary btn-sm text-xs"
                    title="Restore to last saved allocations"
                  >
                    Reset
                  </button>
                </div>
              </div>
            </div>

            {/* Activities Table */}
            {isExpanded && activities.length > 0 && (
              <div className="dp-scroll-container">
                <table className="dp-table">
                  <thead>
                    <tr>
                      <th className="w-6"></th>
                      <th className="text-left min-w-[180px]">Activity</th>
                      <th className="text-left w-20">Phase</th>
                      <th className="text-left w-24">Skill</th>
                      <th className="text-left w-32">Role</th>
                      <th className="text-center w-12">Start</th>
                      <th className="text-center w-14 text-gray-400">Base</th>
                      <th className="text-center w-14">LOE</th>
                      {Array.from({ length: NUM_WEEKS }, (_, i) => {
                        const weekNum = i + 1;
                        const weekDate = getWeekDate(weekNum);
                        return (
                          <th key={i} className="dp-week-header">
                            <div className="dp-week-num">W{weekNum}</div>
                            {weekDate && (
                              <div className="dp-week-date">
                                {weekDate.getDate()} {weekDate.toLocaleDateString('en-GB', { month: 'short' })}
                              </div>
                            )}
                          </th>
                        );
                      })}
                      <th className="w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {activities.map((act) => {
                      const withAlloc = getActivityWithAllocation(act, capability.id);
                      // Use adjusted days (with guardrail factor), ensure it's a valid number
                      const expectedDays = Number.isFinite(withAlloc.adjustedDays) ? withAlloc.adjustedDays : 0;
                      const activityMismatch = Math.abs(withAlloc.allocatedDays - expectedDays) > 0.01;
                      const isUnallocated = expectedDays > 0 && withAlloc.allocatedDays === 0 && !withAlloc.isExcluded;
                      const depViolation = !withAlloc.isExcluded && act.dependencies && act.dependencies.length > 0
                        ? (() => {
                            const weekAllocsMap: Record<string, Record<number, number>> = {};
                            for (const [id, a] of Object.entries(allocations)) weekAllocsMap[id] = a.weekAllocations;
                            return checkDependencyViolation(act.id, weekAllocsMap, act.dependencies.map(d => ({ predecessorId: d.predecessorId, type: d.dependencyType, lagDays: d.lagDays })), dpw);
                          })()
                        : null;
                      const skillColour = getSkillColours(withAlloc.skill, skillsList);
                      const isNotesExpanded = expandedNotes.has(act.id);

                      return (
                        <React.Fragment key={act.id}>
                          <tr
                            className={cn(
                              withAlloc.isModified && 'dp-row-modified',
                              activityMismatch && withAlloc.allocatedDays > 0 && !withAlloc.isExcluded && 'dp-row-mismatch',
                              withAlloc.isExcluded && 'opacity-40 bg-gray-50',
                              criticalPathIds.has(act.id) && 'border-l-2 border-l-red-500'
                            )}
                          >
                            <td className="px-2">
                              <div className="flex items-center gap-1">
                                {act.isMilestone ? (
                                  <button
                                    onClick={() => toggleActivityExcluded(act.id)}
                                    className={cn('text-base leading-none', withAlloc.isExcluded ? 'text-gray-300' : 'text-amber-500')}
                                    title={`Milestone: ${withAlloc.isExcluded ? 'Include' : 'Exclude'}`}
                                  >
                                    ◆
                                  </button>
                                ) : (
                                  <input
                                    type="checkbox"
                                    checked={!withAlloc.isExcluded}
                                    onChange={() => toggleActivityExcluded(act.id)}
                                    className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    title={withAlloc.isExcluded ? 'Include activity' : 'Exclude activity'}
                                  />
                                )}
                              </div>
                            </td>
                            <td>
                              <div className="flex items-center gap-2">
                                <span className={cn('dp-activity-name', withAlloc.isExcluded && 'line-through')}>{withAlloc.name}</span>
                                {withAlloc.isExcluded && (
                                  <span className="dp-activity-badge text-gray-500 bg-gray-100">Excluded</span>
                                )}
                                {criticalPathIds.has(act.id) && (
                                  <span className="dp-activity-badge bg-red-100 text-red-700" title="Critical path — zero float">CP</span>
                                )}
                                {!withAlloc.isExcluded && withAlloc.isModified && (
                                  <span className="dp-activity-badge modified">Modified</span>
                                )}
                                {isUnallocated && (
                                  <span className="dp-activity-badge bg-red-100 text-red-700">Unallocated</span>
                                )}
                                {!withAlloc.isExcluded && activityMismatch && withAlloc.allocatedDays > 0 && (
                                  <span className={cn(
                                    'dp-activity-badge',
                                    withAlloc.allocatedDays > expectedDays ? 'over' : 'short'
                                  )}>
                                    {withAlloc.allocatedDays > expectedDays
                                      ? `+${(withAlloc.allocatedDays - expectedDays).toFixed(1)}`
                                      : `${(expectedDays - withAlloc.allocatedDays).toFixed(1)} short`}
                                  </span>
                                )}
                                {depViolation && (
                                  <span className="dp-activity-badge bg-amber-100 text-amber-700 flex items-center gap-0.5" title={depViolation.message}>
                                    <AlertTriangle className="h-3 w-3" />
                                    Dep
                                  </span>
                                )}
                                <button
                                  onClick={() => toggleNotesExpanded(act.id)}
                                  className={cn(
                                    'p-0.5 rounded transition-colors',
                                    withAlloc.notes
                                      ? 'text-blue-500 hover:text-blue-700 hover:bg-blue-50'
                                      : 'text-gray-300 hover:text-gray-500 hover:bg-gray-100'
                                  )}
                                  title={withAlloc.notes || 'Add notes'}
                                >
                                  <MessageSquare className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            </td>
                            <td>
                              {act.activityPhase && PHASE_COLOURS[act.activityPhase] && (
                                <span
                                  className="inline-block px-1.5 py-0.5 rounded text-[8px] font-bold uppercase tracking-wider"
                                  style={{
                                    backgroundColor: PHASE_COLOURS[act.activityPhase].bg,
                                    color: PHASE_COLOURS[act.activityPhase].text,
                                  }}
                                >
                                  {PHASE_COLOURS[act.activityPhase].label}
                                </span>
                              )}
                            </td>
                            <td>
                              {withAlloc.skill && (
                                <span
                                  className="inline-block px-2.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider"
                                  style={{ backgroundColor: skillColour.bg, color: skillColour.text }}
                                >
                                  {withAlloc.skill}
                                </span>
                              )}
                            </td>
                            <td>
                              {(() => {
                                const rc = getRoleColours(withAlloc.role, roles);
                                return (
                                  <span
                                    className="inline-block px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider"
                                    style={{ backgroundColor: rc.bg, color: rc.text }}
                                  >
                                    {withAlloc.role}
                                  </span>
                                );
                              })()}
                            </td>
                            <td className="dp-start-column">
                              <select
                                value={(() => {
                                  const weeks = Object.keys(withAlloc.allocation.weekAllocations)
                                    .map(Number)
                                    .filter(w => withAlloc.allocation.weekAllocations[w] > 0)
                                    .sort((a, b) => a - b);
                                  return weeks[0] || capabilityStartWeeks[capability.id] || 1;
                                })()}
                                onChange={(e) => updateActivityStartWeek(act.id, parseInt(e.target.value), capability)}
                                className="dp-start-week-select"
                                disabled={withAlloc.isExcluded}
                              >
                                {Array.from({ length: NUM_WEEKS }, (_, i) => (
                                  <option key={i + 1} value={i + 1}>W{i + 1}</option>
                                ))}
                              </select>
                            </td>
                            <td className="text-center text-xs text-gray-400 tabular-nums">
                              {withAlloc.originalAdjustedDays.toFixed(1)}
                            </td>
                            <td className="text-center">
                              <div className="flex items-center justify-center gap-0.5">
                                <input
                                  type="number"
                                  value={expectedDays.toFixed(1)}
                                  onChange={(e) => {
                                    const inputDays = parseFloat(e.target.value || '0');
                                    const gf = withAlloc.guardrailFactor || 1;
                                    const baseDays = gf > 0 ? inputDays / gf : inputDays;
                                    updateActivityDays(act.id, baseDays);
                                  }}
                                  className={cn(
                                    'dp-days-input',
                                    withAlloc.isModified && 'dp-days-modified',
                                    expectedDays > 0 && 'dp-days-has-value'
                                  )}
                                  title={`LOE: ${expectedDays.toFixed(1)} days (base: ${withAlloc.originalAdjustedDays.toFixed(1)}d)`}
                                  step="0.5"
                                  min="0"
                                  disabled={withAlloc.isExcluded}
                                />
                                {withAlloc.isModified && (
                                  <button
                                    onClick={() => resetActivity(act.id)}
                                    className="p-0.5 text-amber-500 hover:text-amber-700 transition-colors flex-shrink-0"
                                    title="Reset to base"
                                  >
                                    <RotateCcw className="h-3 w-3" />
                                  </button>
                                )}
                              </div>
                            </td>
                            {Array.from({ length: NUM_WEEKS }, (_, i) => {
                              const week = i + 1;
                              const days = withAlloc.allocation.weekAllocations[week] || 0;

                              return (
                                <td key={i} className="dp-week-cell">
                                  <input
                                    type="number"
                                    value={days || ''}
                                    onChange={(e) =>
                                      updateWeekAllocation(
                                        act.id,
                                        week,
                                        parseFloat(e.target.value) || 0
                                      )
                                    }
                                    placeholder=""
                                    className={cn(
                                      'dp-week-input',
                                      days > 0 && 'dp-cell-filled'
                                    )}
                                    step="0.5"
                                    min="0"
                                    max={DAYS_PER_WEEK}
                                    disabled={withAlloc.isExcluded}
                                  />
                                </td>
                              );
                            })}
                            <td className="px-1">
                              {withAlloc.allocatedDays > 0 && !withAlloc.isExcluded && (
                                <div className="flex items-center gap-0.5">
                                  <button
                                    onClick={() => shiftActivityWeeks(act.id, -1)}
                                    className="p-0.5 text-gray-400 hover:text-gray-600 transition-colors"
                                    title="Shift left 1 week"
                                  >
                                    <ChevronLeft className="h-3 w-3" />
                                  </button>
                                  <button
                                    onClick={() => shiftActivityWeeks(act.id, 1)}
                                    className="p-0.5 text-gray-400 hover:text-gray-600 transition-colors"
                                    title="Shift right 1 week"
                                  >
                                    <ChevronRight className="h-3 w-3" />
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                          {/* Notes expansion row */}
                          {isNotesExpanded && (
                            <tr className="bg-gray-50">
                              <td></td>
                              <td colSpan={5 + NUM_WEEKS} className="py-2 px-3">
                                <div className="flex items-start gap-2">
                                  <MessageSquare className="h-4 w-4 text-gray-400 mt-1 flex-shrink-0" />
                                  <textarea
                                    value={withAlloc.notes}
                                    onChange={(e) => updateActivityNotes(act.id, e.target.value)}
                                    placeholder="Add notes about this activity..."
                                    className="flex-1 text-sm border border-gray-200 rounded-md px-2 py-1 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                    rows={2}
                                  />
                                </div>
                              </td>
                              <td></td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                    {/* Week Totals Row (activity sum) */}
                    <tr className="dp-capability-totals-header">
                      <td colSpan={8} className="text-right pr-4 text-xs font-semibold text-gray-500">
                        Week Totals
                      </td>
                      {Array.from({ length: NUM_WEEKS }, (_, i) => {
                        const week = i + 1;
                        const total = weekTotals[week] || 0;
                        return (
                          <td key={i} className="dp-capability-total-cell">
                            {total > 0 ? total.toFixed(1) : ''}
                          </td>
                        );
                      })}
                      <td></td>
                    </tr>
                    {/* Architect Oversight Row */}
                    <tr className="dp-oversight-row">
                      <td className="px-2"></td>
                      <td colSpan={2}>
                        <span className="dp-oversight-label">Architect Oversight</span>
                        <span className="dp-oversight-pct">({(architectOversightPct / 100).toFixed(0)}%)</span>
                      </td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td className="text-center text-xs font-medium text-gray-600">
                        {(capBaseDays * (architectOversightPct / 10000)).toFixed(1)}
                      </td>
                      {Array.from({ length: NUM_WEEKS }, (_, i) => {
                        const week = i + 1;
                        const weekTotal = weekTotals[week] || 0;
                        const oversightDays = weekTotal > 0 ? weekTotal * (architectOversightPct / 10000) : 0;
                        return (
                          <td key={i} className="dp-oversight-cell">
                            {oversightDays > 0 ? oversightDays.toFixed(2) : ''}
                          </td>
                        );
                      })}
                      <td></td>
                    </tr>
                    {/* Project Management Row */}
                    <tr className="dp-oversight-row">
                      <td className="px-2"></td>
                      <td colSpan={2}>
                        <span className="dp-oversight-label">Project Management</span>
                        <span className="dp-oversight-pct">({(pmPct / 100).toFixed(0)}%)</span>
                      </td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td className="text-center text-xs font-medium text-gray-600">
                        {(capBaseDays * (pmPct / 10000)).toFixed(1)}
                      </td>
                      {Array.from({ length: NUM_WEEKS }, (_, i) => {
                        const week = i + 1;
                        const weekTotal = weekTotals[week] || 0;
                        const pmDays = weekTotal > 0 ? weekTotal * (pmPct / 10000) : 0;
                        return (
                          <td key={i} className="dp-oversight-cell">
                            {pmDays > 0 ? pmDays.toFixed(2) : ''}
                          </td>
                        );
                      })}
                      <td></td>
                    </tr>
                    {/* Week Totals Row */}
                    <tr className="dp-totals-row">
                      <td colSpan={8} className="text-right pr-4">
                        Week Totals (incl. Oversight)
                      </td>
                      {Array.from({ length: NUM_WEEKS }, (_, i) => {
                        const week = i + 1;
                        const total = weekTotals[week] || 0;
                        const withOversight = total * (1 + (architectOversightPct + pmPct) / 10000);
                        const resourcesThisWeek = weekResourceTotals[week] || {};
                        const overAllocated = isWeekOverAllocated(resourcesThisWeek, dpw);

                        return (
                          <td
                            key={i}
                            className={overAllocated ? 'dp-total-over' : ''}
                          >
                            {total > 0 ? withOversight.toFixed(1) : ''}
                          </td>
                        );
                      })}
                      <td></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            {isExpanded && activities.length === 0 && (
              <div className="px-4 py-6 text-center text-sm text-gray-500">
                No activities defined for this capability
              </div>
            )}

            {/* Guardrails Section - Per Capability */}
            {isExpanded && (capability.guardrails || []).length > 0 && (
              <div className="px-4 py-3 border-t border-gray-100 bg-gray-50/50">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-xs font-medium text-gray-600 flex items-center gap-1.5">
                    <Sliders className="h-3.5 w-3.5" />
                    Guardrails / Multipliers
                  </h4>
                  {guardrailFactor > 1 && (
                    <span className="text-xs text-xtravirt-cyan font-medium">
                      {guardrailFactor.toFixed(2)}x multiplier
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {(capability.guardrails || []).map((gr) => (
                    <div key={gr.id} className="flex items-center justify-between gap-2 bg-white rounded border border-gray-200 px-2.5 py-1.5">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-gray-700 truncate">{gr.name}</p>
                        {gr.multiplierFactor != null && gr.multiplierFactor > 0 ? (
                          <p className="text-[10px] text-xtravirt-cyan font-mono">+{gr.multiplierFactor}x {gr.multiplierLabel || ''}</p>
                        ) : gr.multiplier ? (
                          <p className="text-[10px] text-xtravirt-cyan font-mono">{gr.multiplier}</p>
                        ) : null}
                      </div>
                      <div className="flex-shrink-0">
                        {gr.inputType === 'number' && (
                          <input
                            type="number"
                            value={guardrailValues[gr.id] || gr.defaultValue || ''}
                            onChange={(e) => onGuardrailChange(gr.id, e.target.value)}
                            min={gr.minValue ?? undefined}
                            max={gr.maxValue ?? undefined}
                            className="input w-16 text-center text-xs py-1"
                          />
                        )}
                        {gr.inputType === 'boolean' && (
                          <input
                            type="checkbox"
                            checked={guardrailValues[gr.id] === 'true'}
                            onChange={(e) => onGuardrailChange(gr.id, e.target.checked ? 'true' : 'false')}
                            className="h-4 w-4 rounded border-gray-300 text-xtravirt-cyan focus:ring-xtravirt-cyan"
                          />
                        )}
                        {gr.inputType === 'select' && gr.options && (
                          <select
                            value={guardrailValues[gr.id] || gr.defaultValue || ''}
                            onChange={(e) => onGuardrailChange(gr.id, e.target.value)}
                            className="input text-xs py-1 pr-6"
                          >
                            <option value="">Select...</option>
                            {gr.options.map((option) => (
                              <option key={option} value={option}>{option}</option>
                            ))}
                          </select>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Add Activity Button and Form */}
            {isExpanded && (
              <>
                {showAddActivity === capability.id ? (
                  <div className="dp-add-activity-form">
                    <input
                      type="text"
                      placeholder="Activity name"
                      value={newActivityName}
                      onChange={(e) => setNewActivityName(e.target.value)}
                      className="dp-add-activity-input flex-1"
                      autoFocus
                    />
                    <select
                      value={newActivityRole}
                      onChange={(e) => setNewActivityRole(e.target.value)}
                      className="dp-role-select"
                      style={{ backgroundColor: getRoleColours(newActivityRole, roles).bg, color: getRoleColours(newActivityRole, roles).text }}
                    >
                      {roles.map((role) => (
                        <option key={role.id} value={role.name}>{role.name}</option>
                      ))}
                    </select>
                    <input
                      type="number"
                      placeholder="Days"
                      value={newActivityDays}
                      onChange={(e) => setNewActivityDays(e.target.value)}
                      className="dp-add-activity-input w-20"
                      step="0.5"
                      min="0"
                    />
                    <button
                      onClick={() => handleAddActivity(capability.id)}
                      className="btn-primary btn-sm"
                      disabled={!newActivityName.trim()}
                    >
                      Add
                    </button>
                    <button
                      onClick={() => {
                        setShowAddActivity(null);
                        setNewActivityName('');
                        setNewActivityDays('1.0');
                      }}
                      className="btn-secondary btn-sm"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowAddActivity(capability.id)}
                    className="dp-add-activity-btn"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Add Activity
                  </button>
                )}
              </>
            )}
          </div>
        );
      })}

      {/* Resource Utilisation Panel */}
      <ResourceUtilisationPanel
        selectedCapabilitiesData={selectedCapabilitiesData}
        allocations={allocations}
        daysPerWeek={propDaysPerWeek || DAYS_PER_WEEK}
        numWeeks={NUM_WEEKS}
      />

      {/* Totals Summary */}
      <div className="dp-summary">
        <div className="dp-summary-stat">
          <span className="dp-summary-value highlight">{selectedCapabilitiesData.length}</span> capabilities,{' '}
          <span className="dp-summary-value highlight">{totals.totalActivities}</span> activities
        </div>
        <div className="flex items-center gap-6">
          <div className="dp-summary-stat">
            Total Days:
            <span className="dp-summary-value">
              {totals.totalBaseDays.toFixed(1)}
            </span>
          </div>
          <div className="dp-summary-stat">
            Allocated:
            <span className={cn(
              'dp-summary-value',
              Math.abs(totals.totalAllocatedDays - totals.totalBaseDays) > 0.01
                ? 'danger'
                : 'success'
            )}>
              {totals.totalAllocatedDays.toFixed(1)}
            </span>
          </div>
          {Math.abs(totals.totalAllocatedDays - totals.totalBaseDays) > 0.01 && (
            <div className="dp-summary-stat text-xs">
              <span className="dp-summary-value danger">
                ({(totals.totalBaseDays - totals.totalAllocatedDays).toFixed(1)} remaining)
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============ Version History Tab ============

interface VersionHistoryTabProps {
  projectId: string;
  version: number;
}

function VersionHistoryTab({ projectId, version }: VersionHistoryTabProps) {
  const { data: versionsData, isLoading } = useProjectVersions(projectId);
  const restoreVersion = useRestoreProjectVersion();
  const [restoringId, setRestoringId] = useState<string | null>(null);

  const handleRestore = async (versionId: string) => {
    setRestoringId(versionId);
    try {
      await restoreVersion.mutateAsync({ projectId, versionId });
    } finally {
      setRestoringId(null);
    }
  };

  const versions = versionsData?.versions || [];

  return (
    <div className="space-y-6">
      <div>
        <h3 className="font-heading text-lg font-semibold text-xtravirt-dark mb-4">
          Version History
        </h3>
        <p className="text-sm text-gray-500">
          Track changes and restore previous versions of this project.
        </p>
      </div>

      {/* Current Version */}
      <div className="card overflow-hidden !p-0">
        <div className="px-4 py-3 bg-green-50 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-sm font-medium text-green-700">v{version}</span>
              <span className="text-sm text-green-600 ml-2">Current</span>
            </div>
            <span className="text-xs text-gray-500">
              Unsaved changes
            </span>
          </div>
        </div>

        {/* Version History List */}
        {isLoading ? (
          <div className="px-4 py-8 text-center">
            <LoadingSpinner size="sm" />
          </div>
        ) : versions.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-gray-500">
            <History className="h-8 w-8 mx-auto text-gray-300 mb-3" />
            <p>No saved versions yet. Save the project to create a version.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {versions.map((v) => (
              <div key={v.id} className="px-4 py-3 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900">v{v.version}</span>
                      {v.changedFields && v.changedFields.length > 0 && (
                        <span className="text-xs text-gray-500">
                          Changed: {v.changedFields.join(', ')}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-gray-500">
                        {new Date(v.createdAt).toLocaleString()}
                      </span>
                      {v.createdByEmail && (
                        <span className="text-xs text-gray-400">
                          by {v.createdByEmail}
                        </span>
                      )}
                    </div>
                    {v.changeDescription && (
                      <p className="text-xs text-gray-600 mt-1">{v.changeDescription}</p>
                    )}
                  </div>
                  <button
                    onClick={() => handleRestore(v.id)}
                    disabled={restoringId === v.id}
                    className="btn-secondary btn-sm text-xs"
                  >
                    {restoringId === v.id ? 'Restoring...' : 'Restore'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ============ Helpers ============

function formatCurrency(cents: number, currency: 'GBP' | 'USD' | 'EUR' = 'USD'): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);

  if (diffSec < 60) {
    return 'just now';
  } else if (diffMin < 60) {
    return `${diffMin} minute${diffMin !== 1 ? 's' : ''} ago`;
  } else if (diffHour < 24) {
    return `${diffHour} hour${diffHour !== 1 ? 's' : ''} ago`;
  } else {
    return date.toLocaleString();
  }
}

// ============ Mark As Lost Modal ============

interface MarkLostModalProps {
  projectId: string;
  onClose: () => void;
  onSuccess: () => void;
}

function MarkLostModal({ projectId, onClose, onSuccess }: MarkLostModalProps) {
  const [status, setStatus] = useState<'lost' | 'cancelled'>('lost');
  const [reason, setReason] = useState<LostReason>('no_budget');
  const [notes, setNotes] = useState('');
  const markLost = useMarkProjectLost();

  const handleSubmit = async () => {
    await markLost.mutateAsync({
      projectId,
      status,
      reason,
      notes: notes || null,
    });
    onSuccess();
    onClose();
  };

  return (
    <div className="dialog-overlay">
      <div className="dialog max-w-lg">
        <div className="dialog-header">
          <h2 className="dialog-title">Mark Project as Lost / Cancelled</h2>
        </div>
        <div className="dialog-body space-y-4">
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <div>
                This project will be excluded from pipeline metrics but kept for reporting (win rate, lost reasons). You can reopen it later if needed.
              </div>
            </div>
          </div>

          <div>
            <label className="label">Status</label>
            <div className="flex gap-2 mt-1">
              <button
                type="button"
                onClick={() => setStatus('lost')}
                className={cn(
                  'flex-1 px-3 py-2 rounded-md text-sm font-medium border transition-colors',
                  status === 'lost' ? 'bg-red-100 border-red-300 text-red-800' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                )}
              >
                Lost
              </button>
              <button
                type="button"
                onClick={() => setStatus('cancelled')}
                className={cn(
                  'flex-1 px-3 py-2 rounded-md text-sm font-medium border transition-colors',
                  status === 'cancelled' ? 'bg-orange-100 border-orange-300 text-orange-800' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                )}
              >
                Cancelled
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              <strong>Lost:</strong> Customer decided not to proceed. <strong>Cancelled:</strong> Project started then abandoned.
            </p>
          </div>

          <div>
            <label className="label">Reason *</label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value as LostReason)}
              className="input w-full mt-1"
            >
              {LOST_REASONS.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="label">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="input w-full mt-1"
              placeholder="Additional context (optional)"
            />
          </div>
        </div>
        <div className="dialog-footer">
          <button type="button" onClick={onClose} className="btn-secondary" disabled={markLost.isPending}>
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="btn-primary bg-red-600 hover:bg-red-700"
            disabled={markLost.isPending}
          >
            {markLost.isPending ? 'Saving...' : `Mark as ${status === 'lost' ? 'Lost' : 'Cancelled'}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============ Project Mobilisation Timeline ============

interface ProjectMobilisationTimelineProps {
  projectId: string;
  stages: ProjectMobilisationStageInfo[];
}

function ProjectMobilisationTimeline({ projectId, stages }: ProjectMobilisationTimelineProps) {
  const [expandedStage, setExpandedStage] = useState<MobilisationStage | null>(null);
  const updateStage = useUpdateProjectMobilisationStage();

  const getStageData = (stageId: MobilisationStage): ProjectMobilisationStageInfo | undefined => {
    return stages.find((s) => s.stageId === stageId);
  };

  const handleStatusChange = async (
    stageId: MobilisationStage,
    newStatus: 'not_started' | 'in_progress' | 'completed'
  ) => {
    try {
      await updateStage.mutateAsync({
        projectId,
        stageId,
        status: newStatus,
        completedDate: newStatus === 'completed' ? new Date().toISOString().split('T')[0] : null,
      });
    } catch (error) {
      console.error('Failed to update mobilisation stage:', error);
    }
  };

  const handleFieldUpdate = async (
    stageId: MobilisationStage,
    data: { targetDate?: string | null; notes?: string | null }
  ) => {
    const stageData = getStageData(stageId);
    try {
      await updateStage.mutateAsync({
        projectId,
        stageId,
        status: stageData?.status || 'not_started',
        ...data,
      });
    } catch (error) {
      console.error('Failed to update mobilisation stage:', error);
    }
  };

  return (
    <div className="space-y-1 relative">
      {PROJECT_MOBILISATION_STAGES.map((stage) => {
        const stageData = getStageData(stage.id);
        const status = stageData?.status || 'not_started';
        const isExpanded = expandedStage === stage.id;
        const isCompleted = status === 'completed';
        const isInProgress = status === 'in_progress';

        return (
          <div key={stage.id}>
            <div
              className={cn(
                'flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors',
                isExpanded ? 'bg-gray-50' : 'hover:bg-gray-50'
              )}
              onClick={() => setExpandedStage(isExpanded ? null : stage.id)}
            >
              <div className="flex-shrink-0">
                {isCompleted ? (
                  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-green-500 text-white">
                    <Check className="h-4 w-4" />
                  </div>
                ) : isInProgress ? (
                  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-xtravirt-cyan text-white">
                    <Play className="h-4 w-4" />
                  </div>
                ) : (
                  <div className="flex h-6 w-6 items-center justify-center rounded-full border-2 border-gray-300">
                    <Circle className="h-3 w-3 text-gray-300" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <span
                  className={cn(
                    'font-medium',
                    isCompleted && 'text-green-700',
                    isInProgress && 'text-xtravirt-cyan',
                    !isCompleted && !isInProgress && 'text-gray-500'
                  )}
                >
                  {stage.name}
                </span>
                {stageData?.targetDate && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    Target: {new Date(stageData.targetDate).toLocaleDateString()}
                  </p>
                )}
              </div>
              <div className="flex-shrink-0">
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-gray-400" />
                )}
              </div>
            </div>

            {isExpanded && (
              <div className="ml-6 md:ml-9 mt-2 p-3 md:p-4 bg-gray-50 rounded-lg space-y-4">
                <div>
                  <label className="label text-xs mb-2">Status</label>
                  <div className="flex gap-2">
                    {(['not_started', 'in_progress', 'completed'] as const).map((s) => (
                      <button
                        key={s}
                        onClick={() => handleStatusChange(stage.id, s)}
                        disabled={updateStage.isPending}
                        className={cn(
                          'px-3 py-1.5 rounded text-xs font-medium transition-colors',
                          status === s
                            ? s === 'completed'
                              ? 'bg-green-500 text-white'
                              : s === 'in_progress'
                              ? 'bg-xtravirt-cyan text-white'
                              : 'bg-gray-500 text-white'
                            : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-100'
                        )}
                      >
                        {s === 'not_started'
                          ? 'Not Started'
                          : s === 'in_progress'
                          ? 'In Progress'
                          : 'Completed'}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="label text-xs">Target Date</label>
                  <input
                    type="date"
                    value={stageData?.targetDate || ''}
                    onChange={(e) =>
                      handleFieldUpdate(stage.id, { targetDate: e.target.value || null })
                    }
                    className="input mt-1 w-full text-sm"
                  />
                </div>

                <div>
                  <label className="label text-xs">Notes</label>
                  <textarea
                    value={stageData?.notes || ''}
                    onChange={(e) =>
                      handleFieldUpdate(stage.id, { notes: e.target.value || null })
                    }
                    rows={2}
                    placeholder="Stage notes..."
                    className="input mt-1 w-full text-sm"
                  />
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
